/**
 * Long-form help text for every dashboard page — the prose behind the sidebar
 * hover hint and the (i) button at the top of each page. See ./pageGuides.js
 * for how it is resolved and rendered.
 *
 * Every entry was written by reading the page's own template and code, so the
 * bullets name the real buttons, columns and filters. When you change a page,
 * change its entry here too — a help panel that lies is worse than none.
 *
 * Shape:
 *   tagline  one line, shown under the page title and in the hover hint
 *   what     2-4 sentences: what this page is and when you reach for it
 *   shows    what the page displays (cards, columns, filters)
 *   actions  what the admin can DO here (empty for read-only pages)
 *   notes    caveats worth knowing before you trust or click something
 *   related  neighbouring pages, keyed by route (labels come from menu.js)
 *
 * Keys are matched on the first path segment, so '/Trips' also covers
 * '/Trips/Details/9'.
 */

export const PAGE_GUIDES = {
'/Home': {
        tagline: 'ملخّص شامل لأرقام النظام: الرحلات والمستخدمون والإيرادات',
        what: 'الصفحة الافتتاحية للوحة التحكم، وتجمع في شاشة واحدة أهم مؤشرات المنصة: أعداد المستخدمين والمدارس، الاشتراكات، الإيرادات والمصروفات، وحركة الرحلات خلال الأسبوع والسنة. يستخدمها المسؤول في بداية اليوم ليعرف بسرعة هل النشاط طبيعي أم أن هناك تراجعاً يحتاج متابعة.',
        shows: [
            'بطاقة ترحيب تعرض اسم المستخدم وعدد المستخدمين النشطين وعدد المدارس',
            'بطاقة الاشتراكات النشطة مع نسبة التغيّر ورسم لآخر ستة أشهر',
            'بطاقة الركاب والسائقين مع منحنى المسجّلين الجدد في آخر تسعة أيام',
            'رسم الإيرادات الشهرية لهذه السنة مقابل السنة الماضية مع الإجمالي وصافي الربح والمصروفات',
            'إيرادات الشهر حسب نوع الرحلة (عادية، مدارس، مشتركة) ومعدل اكتمال الرحلات',
            'بطاقتا العملاء الجدد هذا الشهر ودخل اليوم مع نسبة التغيّر',
            'رسم الرحلات المكتملة مقابل الملغاة خلال الأسبوع، وتوزيعها حسب النوع، وجدول آخر الرحلات'
        ],
        actions: [
            'بدّل تبويبات جدول آخر الرحلات بين «رحلات عادية» و«رحلات مشتركة» و«رحلات مدارس»',
            'مرّر المؤشر فوق أي رسم بياني لقراءة قيمة الشهر أو اليوم بدقة',
            'اضغط «إعادة المحاولة» إذا ظهرت رسالة خطأ في التحميل'
        ],
        notes: [
            'تُحمَّل الأرقام مرة واحدة عند فتح الصفحة؛ أعد تحميلها للحصول على أحدث قيمة',
            'إذا لم توجد بيانات لفترة ما تظهر عبارة توضيحية بدل رسم فارغ قد يُفهم كعطل',
            'العناوين القديمة التالفة في قاعدة البيانات تظهر بعبارة «عنوان غير متوفر»'
        ],
        related: [
            { path: '/Statistic', label: 'الإحصائيات' },
            { path: '/Events', label: 'آخر الأحداث' },
            { path: '/CurrentTrips', label: 'الرحلات الجارية' }
        ]
    },

    '/Statistic': {
        tagline: 'رسوم تفصيلية لأداء الرحلات والسائقين والمدارس والمال',
        what: 'صفحة تحليلية أعمق من النظرة العامة، تعرض مقارنات سنوية للإيرادات، وأداء رحلات اليوم، وترتيب أفضل السائقين، وجدولاً لكل مدرسة، إضافة إلى التسويات المالية وبطاقات الشحن والاشتراكات. يستخدمها المسؤول عند إعداد تقرير أو تتبّع سبب ارتفاع أو انخفاض في الأداء.',
        shows: [
            'بطاقة ترحيب بإيرادات الشهر ومصروفاته، وبطاقتا الركاب والسائقين مع نسبة التغيّر',
            'رسم مقارنة الإيرادات بين السنة الحالية والسنة الماضية شهراً بشهر',
            'أداء النظام اليوم: رحلات قيد المعالجة ومعلقة ومكتملة مع نسبة الاكتمال وإجمالي رحلات اليوم',
            'رسم تسجيلات آخر سبعة أيام مع مقارنة هذا الأسبوع بالأسبوع السابق، وتوزيع مصادر الدخل',
            'جدول المدارس: اسم المدرسة وعدد الطلاب وعدد الرحلات والإيرادات والاشتراكات النشطة',
            'التسويات المالية: إجمالي أرصدة المحافظ والرصيد المتاح وإجمالي المسحوبات شهرياً',
            'توزيع الرحلات حسب النوع، وأفضل عشرة سائقين بالمكتملة والملغاة ومتوسط تقييمهم'
        ],
        actions: [
            'مرّر المؤشر فوق أي رسم لمعرفة القيمة الدقيقة لكل شهر أو يوم أو سائق',
            'اضغط «إعادة المحاولة» إذا فشل تحميل البيانات'
        ],
        notes: [
            'الصفحة للعرض والتحليل فقط ولا تسمح بتعديل أي بيان',
            'إحصائيات بطاقات الشحن تخص الشهر الحالي فقط'
        ],
        related: [
            { path: '/Home', label: 'نظرة عامة' },
            { path: '/Events', label: 'آخر الأحداث' },
            { path: '/RevenueDashboard', label: 'الإيرادات والأرباح' }
        ]
    },

    '/Events': {
        tagline: 'أحدث أحداث النظام مع مؤشرات اليوم وأكثر المدن نشاطاً',
        what: 'لوحة متابعة يومية تجمع مؤشرات النشاط الحالي مع جدول زمني لآخر ما جرى في المنصة من إنشاء رحلات وإكمالها وإلغائها وتسجيل مستخدمين جدد. يفتحها المسؤول ليعرف ماذا حدث للتو، وأي المدن تولّد أكبر عدد من الرحلات.',
        shows: [
            'أربع بطاقات: رحلات اليوم، الرحلات المكتملة، السائقين النشطين، الركاب النشطين',
            'القيمة الحالية: الدخل والمصروفات وإجمالي السنة مع منحنى آخر سبعة أيام ونسبة التغيّر',
            'التوزيع السنوي للإيرادات حسب نوع الرحلة، والأرباح الشهرية، وأكثر المدن نشاطاً',
            'المبيعات السنوية شهراً بشهر (إيرادات مقابل مصروفات) مع إجمالي كلٍّ منهما',
            'نشاط التطبيق: عدد طلبات اليوم ونسبة القبول، وبطاقتا الركاب والسائقين',
            'الإيرادات حسب الفئة لآخر ستة أشهر، ومؤشرات اكتمال الرحلات ورضا الركاب ونشاط السائقين',
            'جدول زمني بآخر الأحداث موضّح فيه نوع الحدث ووصفه ووقته وتاريخه'
        ],
        actions: [
            'اختر الشهر من القائمة المنسدلة فوق رسم «أكثر المدن نشاطاً» لتحديث ترتيب المدن',
            'مرّر المؤشر فوق الرسوم لقراءة القيم التفصيلية',
            'اضغط «إعادة المحاولة» عند فشل تحميل البيانات'
        ],
        notes: [
            'أنواع الأحداث المعروضة: رحلة جديدة، رحلة مكتملة، رحلة ملغاة، سائق جديد، راكب جديد',
            'تغيير الشهر يحدّث رسم المدن فقط ولا يغيّر بقية أرقام الصفحة'
        ],
        related: [
            { path: '/Home', label: 'نظرة عامة' },
            { path: '/Statistic', label: 'الإحصائيات' },
            { path: '/CurrentTrips', label: 'الرحلات الجارية' }
        ]
    },

    '/LiveTracking': {
        tagline: 'خريطة حيّة لكل الرحلات النشطة ومسار كل سائق',
        what: 'شاشة المتابعة اللحظية: تعرض كل رحلة نشطة كعلامة متحركة على الخريطة، مع قائمة جانبية قابلة للبحث والتصفية للانتقال إلى أي رحلة. يستخدمها المسؤول لمعرفة أين يوجد كل سائق الآن، ولمتابعة شكوى أو حالة تأخير أثناء وقوعها.',
        shows: [
            'شارة حالة الاتصال بالبث الحي: متصل أو جاري الاتصال أو غير متصل، مع وقت آخر تحديث',
            'عدّاد الرحلات الظاهرة من إجمالي الرحلات النشطة',
            'قائمة جانبية بكل رحلة: رقمها ونوعها واسم السائق والراكب والمركبة والحالة ومنذ متى آخر تحديث',
            'علامات ملوّنة على الخريطة حسب نوع الرحلة مع مفتاح ألوان: عادية، مشتركة، مدرسية، أخرى',
            'بطاقة تفاصيل للرحلة المختارة: الراكب والسائق والمركبة وبداية الرحلة والوصول المتوقع والسرعة'
        ],
        actions: [
            'ابحث باسم السائق أو الراكب أو رقم المركبة أو رقم الرحلة',
            'صفّي الرحلات بأزرار النوع: الكل، عادية، مشتركة، مدرسية',
            'رتّب القائمة حسب آخر تحديث أو اسم السائق أو اسم الراكب أو الحالة',
            'اضغط على رحلة في القائمة أو على علامتها لتقريب الخريطة إليها',
            'اضغط «عرض المسار» لرسم خط سير الرحلة، و«مسح» لإزالته',
            'اضغط «فتح الكاميرا» لعرض بث الرحلة إن كانت مزوّدة بكاميرا'
        ],
        notes: [
            'ظهور «غير متصل» يعني توقف البث الحي وعدم تحديث المواقع؛ لا تعتمد على الشاشة حينها',
            'الإحداثيات غير المنطقية أو خارج ليبيا تُتجاهل وتبقى العلامة في آخر موقع صحيح',
            'زر الكاميرا معطّل للرحلات التي لا كاميرا فيها'
        ],
        related: [
            { path: '/Cameras', label: 'الكاميرات المباشرة' },
            { path: '/CurrentTrips', label: 'الرحلات الجارية' },
            { path: '/Sos', label: 'بلاغات الطوارئ' }
        ]
    },

    '/Cameras': {
        tagline: 'بث مباشر من كاميرات الرحلات الجارية الآن',
        what: 'معرض للبثوث المباشرة: يعرض كل كاميرا تسجّل حالياً في رحلة جارية، ويتيح فتح أي منها في مشغّل داخل الصفحة. يستخدمها المسؤول للتحقق البصري من موقف داخل المركبة، أو لمتابعة رحلة عليها ملاحظة أو بلاغ.',
        shows: [
            'عدّاد البثوث الظاهرة من إجمالي البثوث النشطة، وشارة التحديث التلقائي ووقت آخر تحديث',
            'قائمة جانبية ببطاقات البث: رقم الرحلة وعلامة «مباشر» واسم السائق والراكب والمركبة',
            'وقت بدء كل بث ومنذ متى بدأ، ومعرّف البث حين لا تتوفر بيانات الرحلة',
            'مشغّل البث للرحلة المختارة مع اسم السائق والمركبة ونوع الرحلة',
            'وسم «حفظ قانوني» على التسجيلات المحفوظة لأسباب قانونية',
            'شريط تنبيه أحمر عند تعذّر تحميل قائمة البث'
        ],
        actions: [
            'ابحث برقم الرحلة أو اسم السائق أو المركبة أو معرّف البث',
            'اضغط «تحديث الآن» لجلب قائمة البثوث فوراً',
            'اختر بطاقة من القائمة لتشغيل بثها المباشر',
            'اضغط «إغلاق» لإيقاف العرض والعودة إلى الشاشة الفارغة',
            'اضغط «إعادة المحاولة الآن» داخل المشغّل إذا تعثّر البث'
        ],
        notes: [
            'تُحدَّث القائمة تلقائياً كل عشر ثوانٍ، ويتوقف التحديث عند تصغير التبويب ويعود بعد الرجوع إليه',
            'عند انتهاء الرحلة يختفي بثها من القائمة ويُغلق المشغّل تلقائياً',
            'أسماء السائق والراكب قد لا تظهر أحياناً فيُعرض معرّف البث بدلاً منها'
        ],
        related: [
            { path: '/LiveTracking', label: 'التتبع المباشر' },
            { path: '/CurrentTrips', label: 'الرحلات الجارية' }
        ]
    },

    '/CurrentRequests': {
        tagline: 'طلبات الركاب المعلّقة التي لم يقبلها سائق بعد',
        what: 'قائمة الطلبات التي ما زالت في مرحلة «قيد الانتظار» ولم يُسند إليها سائق. هذه شاشة التدخل اليدوي: منها يتابع المسؤول الطلبات العالقة ويعيّن لها سائقاً أو يلغيها إن لم تعد صالحة.',
        shows: [
            'أربع بطاقات إحصائية: إجمالي الرحلات، قيد الانتظار، مكتملة، ملغاة',
            'جدول بالأعمدة: رقم الرحلة، الراكب واسم السائق تحته، نوع الرحلة، المرحلة، الحالة، الإعدادات',
            'شارات ملوّنة لنوع الرحلة (مجدولة، مشتركة، مدرسية) ولمرحلتها وحالتها (نشط أو غير نشط)',
            'تلميح عند المرور على أيقونة الوقت يعرض تاريخ الإنشاء وسعر الرحلة',
            'شاشة تفاصيل للرحلة بتبويبات: التفاصيل الأساسية، الراكب، الفئة، المحادثات، سجل المراحل، التقييمات'
        ],
        actions: [
            'ابحث برقم الرحلة أو العنوان، وصفّي حسب نوع الرحلة أو الحالة',
            'اضغط زر التحديث لإعادة جلب القائمة',
            'افتح «عرض التفاصيل الكاملة» لمطالعة بيانات الرحلة والراكب والسائق والتسعير',
            'اضغط «تعيين سائق» ثم اختر السائق ومركبته (اختيارية) واضغط «تعيين السائق»',
            'اضغط «إلغاء الرحلة» وأكّد العملية لإلغاء طلب لم يعد مطلوباً',
            'تنقّل بين الصفحات وغيّر عدد الصفوف المعروضة من أسفل الجدول'
        ],
        notes: [
            'الصفحة مخصّصة للمرحلة «قيد الانتظار» فقط؛ الرحلات التي بدأت تظهر في صفحة الرحلات الجارية',
            'خيار «تعيين سائق» يظهر للطلبات التي ما زالت قيد الانتظار فقط',
            'زرّا الطباعة والتنزيل في أعلى الجدول معروضان دون وظيفة فعلية حالياً'
        ],
        related: [
            { path: '/CurrentTrips', label: 'الرحلات الجارية' },
            { path: '/Trips', label: 'كل الرحلات' },
            { path: '/Drivers', label: 'السائقون' }
        ]
    },

    '/CurrentTrips': {
        tagline: 'الرحلات قيد التنفيذ الآن من لحظة القبول حتى الانتهاء',
        what: 'قائمة الرحلات التي أُسند لها سائق وما زالت جارية: تم القبول، أو وصل السائق، أو بدأت الرحلة. يستخدمها المسؤول لمتابعة ما يجري فعلياً على الأرض، ولإلغاء رحلة متعثّرة عند الضرورة.',
        shows: [
            'أربع بطاقات إحصائية: إجمالي الرحلات، قيد الانتظار، مكتملة، ملغاة',
            'جدول بالأعمدة: رقم الرحلة، الراكب واسم السائق تحته، نوع الرحلة، المرحلة، الحالة، الإعدادات',
            'شارة المرحلة توضّح إن كانت الرحلة مقبولة أو وصل إليها السائق أو بدأت فعلاً',
            'تلميح بتاريخ إنشاء الرحلة وسعرها عند المرور على أيقونة الوقت',
            'شاشة تفاصيل بتبويبات: التفاصيل الأساسية، الراكب، الفئة، المحادثات، سجل المراحل، التقييمات'
        ],
        actions: [
            'ابحث برقم الرحلة أو العنوان، وصفّي حسب نوع الرحلة أو الحالة',
            'اضغط زر التحديث لإعادة جلب أحدث قائمة',
            'افتح «عرض التفاصيل الكاملة» لمعرفة نقطة الانطلاق والوجهة والمسافة والسعر والتقييم',
            'راجع «سجل المراحل» لمعرفة توقيت كل خطوة وسبب أي إلغاء ورسومه',
            'اضغط «إلغاء الرحلة» وأكّد العملية لإيقاف رحلة جارية',
            'تنقّل بين الصفحات وغيّر عدد الصفوف المعروضة'
        ],
        notes: [
            'لا تظهر هنا الطلبات المعلّقة ولا الرحلات المكتملة أو الملغاة',
            'خيار «تعيين سائق» لا يظهر عملياً في هذه الصفحة لأن كل رحلاتها لديها سائق بالفعل'
        ],
        related: [
            { path: '/CurrentRequests', label: 'الطلبات المعلّقة' },
            { path: '/LiveTracking', label: 'التتبع المباشر' },
            { path: '/Trips', label: 'كل الرحلات' }
        ]
    },

    '/SchoolTripsLive': {
        tagline: 'لوحة حيّة لرحلات الباص المدرسي الجارية الآن',
        what: 'شاشة تشغيل يومية لرحلات المدارس: بطاقة لكل رحلة قيد التنفيذ تعرض المدرسة والسائق وأعداد الطلاب في كل حالة. يفتحها مسؤول النظام أو مسؤول المدرسة في وقت الذهاب والعودة لمتابعة صعود الطلاب ونزولهم لحظة بلحظة.',
        shows: [
            'بطاقة لكل رحلة مدرسية جارية تحمل اسم المدرسة ورقم الرحلة ومرحلتها الحالية',
            'اسم السائق ورقم هاتفه قابلاً للاتصال المباشر',
            'أربعة عدّادات لكل رحلة: قيد الانتظار، صعدوا، نزلوا، غائب',
            'إجمالي عدد الطلاب المرتبطين بالرحلة',
            'وقت آخر تحديث للوحة، ورسالة واضحة حين لا توجد رحلات قيد التنفيذ'
        ],
        actions: [
            'اضغط «تحديث» لجلب أحدث حالة فوراً دون انتظار التحديث التلقائي',
            'اضغط رقم هاتف السائق للاتصال به مباشرة',
            'اضغط «بدء الرحلة» لتشغيل رحلة ما زالت معلّقة، بعد تأكيد الرسالة',
            'اضغط «إنهاء الرحلة» لإقفال رحلة بدأت فعلاً، بعد تأكيد الرسالة'
        ],
        notes: [
            'تتحدّث اللوحة تلقائياً كل خمس عشرة ثانية',
            'يرى مسؤولو المدارس رحلات مدرستهم فقط، بينما يرى مسؤول النظام كل الرحلات',
            'لون الشريط العلوي لكل بطاقة يدل على مرحلة الرحلة'
        ],
        related: [
            { path: '/SchoolTrips', label: 'المسارات المدرسية' },
            { path: '/LiveTracking', label: 'التتبع المباشر' },
            { path: '/CurrentTrips', label: 'الرحلات الجارية' }
        ]
    },

    '/Sos': {
        tagline: 'لوحة عمليات لبلاغات الاستغاثة الحيّة وسجلّها',
        what: 'مركز التعامل مع بلاغات الطوارئ: يعرض البلاغات النشطة على خريطة وقائمة، ويتيح للمشغّل إقفال البلاغ بعد معالجته أو تعليمه كإنذار كاذب. كما يوفّر وضع سجل تاريخي للرجوع إلى بلاغات سابقة ضمن فترة محددة.',
        shows: [
            'أربع بطاقات: بلاغات نشطة الآن، في الانتظار، مُسندة لمشغّل، حُلَّت اليوم',
            'خريطة بعلامات ملوّنة حسب حالة البلاغ، وبنافذة منبثقة فيها اسم صاحب البلاغ وهاتفه ووقته',
            'قائمة البلاغات: اسم المستخدم وحالته ورقم هاتفه ووقت التشغيل وفئة البلاغ وعنوانه',
            'مبدّل بين وضعي «النشطة فقط» و«السجل التاريخي»',
            'مرشّحات السجل: الحالة (مُشغَّلة، مُسندة، محلولة، إنذار كاذب، مُلغاة) وتاريخ من وإلى'
        ],
        actions: [
            'بدّل بين عرض البلاغات النشطة فقط والسجل التاريخي',
            'صفّي السجل حسب الحالة وحدّد فترة زمنية بتاريخي «من» و«إلى»',
            'اضغط على بلاغ في القائمة لتركيز الخريطة على موقعه',
            'اضغط «حل» لإقفال البلاغ بعد معالجته',
            'اضغط «إنذار كاذب» للبلاغات التي تبيّن عدم صحتها',
            'اضغط «تحديث» لإعادة تحميل القائمة يدوياً'
        ],
        notes: [
            'تُحدَّث القائمة تلقائياً كل عشر ثوانٍ، ويظهر أي بلاغ جديد فور تشغيله دون انتظار',
            'زرّا «حل» و«إنذار كاذب» يظهران للبلاغات المُشغَّلة أو المُسندة فقط',
            'البلاغات بلا إحداثيات لا تظهر على الخريطة لكنها تبقى في القائمة'
        ],
        related: [
            { path: '/LiveTracking', label: 'التتبع المباشر' },
            { path: '/TrustedContacts', label: 'جهات الاتصال الموثوقة' },
            { path: '/Reports', label: 'سجل البلاغات والشكاوى' }
        ]
    },

'/Trips': {
    tagline: 'سجل كل الرحلات مع متابعة مراحلها والتدخل فيها',
    what: 'هذه الصفحة هي السجل الشامل لكل رحلات المنصة بجميع أنواعها: العادية والمشتركة والمدرسية والنسائية. يستخدمها المسؤول لمتابعة أي رحلة من لحظة الطلب حتى الاكتمال أو الإلغاء، ولمعرفة من الراكب ومن السائق وكم كان السعر. كما تتيح التدخل اليدوي عند تعثر الرحلة، مثل إسناد سائق أو تحريك مرحلة الرحلة أو إلغائها.',
    shows: [
        'أربع بطاقات علوية: إجمالي الرحلات، وقيد الانتظار، والمكتملة، والملغاة',
        'جدول بأعمدة: رقم الرحلة، الراكب واسم السائق تحته، نوع الرحلة، المرحلة، الحالة',
        'حقل بحث برقم الرحلة أو العنوان يعمل فور الكتابة',
        'فلتر «كل أنواع الرحلات»: عادية، مشتركة، مدرسية، نسائية',
        'فلتر «كل المراحل» من قيد الانتظار حتى الملغاة، وفلتر «كل الحالات» نشط أو غير نشط',
        'تلميح عند مرور المؤشر يعرض تاريخ الإنشاء وسعر الرحلة بالدينار',
        'شاشة تفاصيل بستة تبويبات: التفاصيل الأساسية، الراكب، الفئة، المحادثات، سجل المراحل، التقييمات'
    ],
    actions: [
        'افتح «عرض التفاصيل الكاملة» لرؤية المسافة والسعر المقدر والنهائي ونقطتي الانطلاق والوجهة',
        'اضغط «تعيين سائق» للرحلات قيد الانتظار، واختر السائق ثم المركبة اختيارياً',
        'اضغط «تسجيل وصول السائق» بعد قبول الرحلة لتحريكها إلى المرحلة التالية',
        'اضغط «بدء الرحلة» بعد وصول السائق، ثم «إنهاء الرحلة» بعد انطلاقها',
        'اضغط «إلغاء الرحلة» لأي رحلة لم تكتمل ولم تُلغَ بعد، مع تأكيد العملية',
        'راجع تبويب «سجل المراحل» لمعرفة سبب الإلغاء ورسومه إن وُجدت',
        'استخدم زر التحديث لإعادة جلب القائمة وأشرطة التنقل لتصفح الصفحات'
    ],
    notes: [
        'ترتيب المراحل مُلزَم: لا يظهر زر المرحلة إلا إذا كانت الرحلة في المرحلة التي تسبقها',
        'زرّا الطباعة والتنزيل في أعلى الصفحة غير مفعّلين في هذه الصفحة'
    ],
    related: [
        { path: '/ScheduledTrips', label: 'الرحلات المجدولة' },
        { path: '/SharedTrips', label: 'الرحلات المشتركة' },
        { path: '/CurrentTrips', label: 'الرحلات الجارية' }
    ]
},

'/ScheduledTrips': {
    tagline: 'الرحلات المحجوزة لوقت لاحق مع فلترة زمنية دقيقة',
    what: 'صفحة مخصصة للرحلات المجدولة، أي التي حجزها الراكب ليتم تنفيذها في وقت لاحق. توجد لأن هذه الرحلات تحتاج متابعة مسبقة قبل موعدها، خصوصاً للتأكد من إسناد سائق لها في الوقت المناسب. يستخدمها المسؤول للتحقق من الحجوزات القادمة خلال فترة زمنية محددة.',
    shows: [
        'بطاقات علوية: إجمالي الرحلات، وقيد الانتظار، والمكتملة، والملغاة',
        'فلتر فترة زمنية بحقلي «من» و«إلى» بالتاريخ والساعة مع زر لمسح الفترة',
        'جدول بأعمدة: رقم الرحلة، الراكب والسائق، نوع الرحلة، المرحلة، الحالة',
        'حقل بحث برقم الرحلة أو العنوان، وفلتر المراحل، وفلتر الحالة نشط أو غير نشط',
        'شاشة تفاصيل بتبويبات: التفاصيل الأساسية، الراكب، الفئة، المحادثات، سجل المراحل، التقييمات'
    ],
    actions: [
        'حدد فترة «من» و«إلى» لعرض الحجوزات الواقعة ضمنها فقط',
        'افتح «عرض التفاصيل الكاملة» لمراجعة العنوانين والسعر المقدر والنهائي وبيانات السائق',
        'اضغط «تعيين سائق» للرحلات التي ما زالت قيد الانتظار قبل حلول موعدها',
        'اضغط «إلغاء الرحلة» لأي رحلة لم تكتمل ولم تُلغَ، مع تأكيد العملية',
        'استخدم زر التحديث بعد كل تغيير لإعادة جلب أحدث بيانات القائمة'
    ],
    notes: [
        'لا تتيح هذه الصفحة تحريك مراحل الرحلة يدوياً؛ استخدم صفحة كل الرحلات لذلك'
    ],
    related: [
        { path: '/Trips', label: 'كل الرحلات' },
        { path: '/CurrentRequests', label: 'الطلبات المعلّقة' }
    ]
},

'/SharedTrips': {
    tagline: 'متابعة الرحلات التي يتقاسمها أكثر من راكب',
    what: 'صفحة تعرض الرحلات المشتركة التي ينضم إليها أكثر من راكب على نفس المسار. توجد لفصل هذا النوع عن باقي الرحلات لأن متابعته تختلف، خصوصاً من حيث انتظار السائق وحالة كل رحلة. يستخدمها المسؤول لمراقبة سير هذه الرحلات والتدخل عند بقائها بلا سائق.',
    shows: [
        'بطاقات علوية: إجمالي الرحلات، وقيد الانتظار، والمكتملة، والملغاة',
        'جدول بأعمدة: رقم الرحلة، الراكب واسم السائق، نوع الرحلة، المرحلة، الحالة',
        'حقل بحث برقم الرحلة أو العنوان يعمل فور الكتابة',
        'فلتر المراحل من قيد الانتظار حتى الملغاة، وفلتر الحالة نشط أو غير نشط',
        'شاشة تفاصيل بتبويبات: التفاصيل الأساسية، الراكب، الفئة، المحادثات، سجل المراحل، التقييمات'
    ],
    actions: [
        'افتح «عرض التفاصيل الكاملة» لمعرفة المسافة والسعرين المقدر والنهائي والتقييم',
        'اضغط «تعيين سائق» للرحلات قيد الانتظار مع اختيار المركبة اختيارياً',
        'اضغط «إلغاء الرحلة» لأي رحلة لم تكتمل ولم تُلغَ بعد',
        'راجع تبويب المحادثات للاطلاع على الرسائل المتبادلة داخل الرحلة'
    ],
    notes: [
        'لا توجد هنا فلترة بالتاريخ؛ اعتمد على البحث وفلتر المراحل لتضييق النتائج'
    ],
    related: [
        { path: '/Trips', label: 'كل الرحلات' },
        { path: '/ScheduledTrips', label: 'الرحلات المجدولة' }
    ]
},

'/SchoolTrips': {
    tagline: 'إنشاء مسارات الحافلات المدرسية وإدارة طلابها وسائقيها',
    what: 'هذه الصفحة هي مركز إدارة المسارات المدرسية: كل مسار يربط سائقاً بمدرسة وبمجموعة طلاب خلال فترة زمنية محددة بمواعيد وصول وانصراف. توجد لأن النقل المدرسي متكرر ومنظم مسبقاً وليس طلباً لحظياً. يستخدمها المسؤول أو مسؤول المدرسة لبناء المسارات وتعديلها ومتابعة تنفيذها يومياً.',
    shows: [
        'بطاقات علوية: إجمالي الرحلات، والرحلات النشطة، والمنتهية، ورحلات اليوم',
        'جدول بأعمدة: السائق، المدرسة، الوقت، الفترة من تاريخ إلى تاريخ، عدد الطلاب، الحالة',
        'فلتر «جميع المدارس» وفلتر «جميع السائقين» مع حقل بحث بالاسم أو رقم الهاتف',
        'شاشة ترتيب المسار تعرض كل طالب برقم ترتيب التقاطه على الطريق',
        'شاشة تفاصيل الرحلة بعدادات: عدد الطلاب، ومن وصل، ومن ينتظر، ومن تغيّب',
        'تبويبات تفاصيل الرحلة: معلومات الرحلة، الطلاب، سجل الرحلة'
    ],
    actions: [
        'اضغط زر الإضافة لإنشاء مسار جديد بتحديد السائق والمدرسة ووقتي الوصول والانصراف والفترة والطلاب',
        'اختر «تعديل الرحلة» لتغيير السائق أو المواعيد أو قائمة الطلاب المرتبطين',
        'اختر «إدارة طلاب الرحلة» لإضافة أو إزالة طالب واحد من المسار مباشرة',
        'اختر «ترتيب المسار» ثم عدّل الأرقام واضغط «حفظ ترتيب المسار» ليتبعه تطبيق السائق حرفياً',
        'اختر «تغيير السائق» لإسناد المسار لسائق آخر، أو «تبديل المجموعة لسائق آخر» لنقل كل الطلاب',
        'من تفاصيل الرحلة اضغط «بدء الرحلة» ثم «إنهاء الرحلة» حسب مرحلتها الحالية',
        'اضغط «وصول الحافلة» أمام اسم الطالب لإرسال إشعار لولي أمره أثناء سير الرحلة'
    ],
    notes: [
        'زر «إعادة الترقيم» يعيد ترقيم الطلاب من واحد فصاعداً، ولا يُقبل تكرار نفس الرقم',
        'خيار «تبديل المجموعة لسائق آخر» متاح لمالك المدرسة فقط وينقل كل الطلاب دفعة واحدة',
        'زر «كاميرا الحافلة» يعرض تنبيهاً بأن الكاميرات لم تُركَّب في الحافلات بعد'
    ],
    related: [
        { path: '/SchoolTripsLive', label: 'الرحلات المدرسية الآن' },
        { path: '/Students', label: 'الطلاب' },
        { path: '/Drivers', label: 'السائقون' }
    ]
},

'/Drivers': {
    tagline: 'اعتماد السائقين ووثائقهم ومركباتهم وإدارة حالتهم',
    what: 'الصفحة المرجعية لكل السائقين المسجلين في المنصة، وهي بوابة اعتمادهم قبل السماح لهم بالعمل. يراجع فيها المسؤول بيانات السائق ووثائقه من جواز ورخصة وصورة شخصية، ثم يوافق أو يرفض. كما تُستخدم لتفعيل السائق أو إيقافه أو تعليقه بسبب، ولمراجعة مركباته والموافقة عليها.',
    shows: [
        'بطاقات علوية: العدد الكلي، والمرفوضون، وقيد المراجعة، والموافق عليهم',
        'جدول بأعمدة: الاسم مع رقم التعريف، الهاتف، الحالة، مفعّل، عدد المركبات، عدد الرحلات',
        'شارات إضافية بجانب الحالة توضّح الموقوف والمعطّل وسبب الرفض إن وُجد',
        'شارة تبيّن ما إذا كان السائق مزوّداً بكاميرا أو بدونها',
        'فلتر الحالات: قيد المراجعة، موافق عليه، مرفوض، مع حقل بحث فوري',
        'نافذة عرض تفصيلية تضم الوثائق: جواز السفر، رخصة القيادة، الصورة الشخصية وتواريخ الانتهاء',
        'شاشة مركبات السائق بأعمدة: النوع، الموديل، السنة، اللوحة، المقاعد، التأمين، الفحص، المركبة الأساسية'
    ],
    actions: [
        'اضغط زر الإضافة ثم ابحث برقم الهاتف أولاً، فإن وُجد السائق تُعبّأ بياناته وإلا أدخلها كسائق جديد',
        'اختر «موافقة» أو «رفض» للسائقين الذين ما زالوا قيد المراجعة',
        'اختر «تفعيل السائق» أو «إيقاف السائق» للتحكم في قدرته على العمل فوراً',
        'اختر «تعليق» وأدخل سبب التعليق، أو «إلغاء التعليق» لإعادته للخدمة',
        'اختر «عرض المركبات» ثم وافق على المركبة أو ارفضها أو عيّنها كمركبة أساسية أو احذفها',
        'اضغط زر الطباعة لطباعة قائمة الصفحة، أو زر التحميل لتنزيل ملف بيانات يفتح ببرامج الجداول',
        'اختر «إلغاء من المدرسة» لفك ارتباط السائق بالمدرسة الحالية عند العمل بحساب مدرسة'
    ],
    notes: [
        'رفع الصور مقيّد بالصور فقط وبحجم أقصى خمسة ميجابايت للصورة الواحدة',
        'الضغط على أي صورة وثيقة يفتحها بحجم مكبّر للمراجعة',
        'خيار «إلغاء من المدرسة» يظهر لحسابات المدارس فقط ولا يحذف السائق من المنصة'
    ],
    related: [
        { path: '/SuspendedDrivers', label: 'السائقون الموقوفون' },
        { path: '/Vehicles', label: 'المركبات' },
        { path: '/DriversRatings', label: 'تقييمات السائقين' }
    ]
},

'/SuspendedDrivers': {
    tagline: 'قائمة السائقين الموقوفين وأسباب إيقافهم وإعادتهم للخدمة',
    what: 'صفحة مركّزة على السائقين الذين جرى تعليقهم عن العمل، تفصلهم عن القائمة العامة حتى لا تضيع حالاتهم بينها. يستخدمها المسؤول لمراجعة سبب إيقاف كل سائق وتقدير ما إذا كان يستحق العودة للخدمة، ولمعرفة حجم ارتباطاته من مركبات ورحلات قبل اتخاذ القرار.',
    shows: [
        'بطاقات علوية: عدد السائقين الموقوفين، والمرتبطون بمركبات، والمرتبطون برحلات، ومن لديهم سبب إيقاف',
        'جدول بأعمدة: الاسم مع رقم التعريف، الهاتف، الحالة، مفعّل، عدد المركبات، عدد الرحلات',
        'عمود «سبب ايقاف» يعرض السبب الكامل عند مرور المؤشر فوقه',
        'حقل بحث فوري لتصفية القائمة، مع أشرطة تنقل بين الصفحات'
    ],
    actions: [
        'مرّر المؤشر على «سبب الإيقاف» لقراءة السبب المسجّل قبل اتخاذ أي قرار',
        'اختر «إلغاء التعليق» لإعادة السائق إلى الخدمة بعد التأكيد',
        'اختر «تعليق» وأدخل سبباً جديداً إذا لزم تحديث سبب الإيقاف'
    ],
    notes: [
        'خيارا التعليق وإلغائه يظهران للسائقين الموافق عليهم فقط',
        'الصفحة للعرض والمعالجة فقط ولا تسمح بتعديل بيانات السائق أو حذفه'
    ],
    related: [
        { path: '/Drivers', label: 'السائقون' },
        { path: '/SuspendedVehicles', label: 'المركبات الموقوفة' }
    ]
},

'/DriversRatings': {
    tagline: 'كل تقييمات الركاب للسائقين في مكان واحد',
    what: 'صفحة عرض فقط تجمع تقييمات الركاب لكل السائقين مرتبطة برقم الرحلة التي تمت فيها. توجد لقياس جودة الخدمة ورصد السائقين الذين تتكرر شكاوى الركاب منهم. يستخدمها المسؤول عند مراجعة أداء السائقين أو قبل اتخاذ إجراء بحق أحدهم.',
    shows: [
        'بطاقات علوية: إجمالي التقييمات، ومتوسط التقييم، وعدد تقييمات الخمس نجوم، وعدد الثلاث نجوم فأقل',
        'جدول بأعمدة: السائق، الراكب، رقم الرحلة، التقييم، الوصف، التاريخ',
        'عمود التقييم يعرض النجوم مع الرقم بجانبها',
        'عمود الوصف يعرض بداية التعليق ونصه الكامل عند مرور المؤشر عليه',
        'حقل بحث فوري وقائمة لتحديد عدد الصفوف المعروضة'
    ],
    actions: [
        'ابحث باسم السائق أو الراكب لتصفية التقييمات المعروضة',
        'غيّر عدد الصفوف المعروضة في الصفحة الواحدة من القائمة المخصصة لذلك',
        'اضغط زر التحديث لإعادة جلب أحدث التقييمات'
    ],
    notes: [
        'الصفحة للاطلاع فقط: لا يمكن تعديل تقييم أو حذفه أو الرد عليه من هنا'
    ],
    related: [
        { path: '/Drivers', label: 'السائقون' },
        { path: '/Reports', label: 'سجل البلاغات والشكاوى' }
    ]
},

'/Vehicles': {
    tagline: 'سجل المركبات ودرجاتها ووثائقها وربطها بالسائقين',
    what: 'صفحة تجمع كل المركبات المسجلة في المنصة مع السائق المالك لكل منها ودرجتها ووثائق تأمينها وفحصها. توجد لأن درجة المركبة وصلاحية وثائقها تحدد نوع الرحلات المسموح لها بتنفيذها. يستخدمها المسؤول لإضافة مركبة أو تعديل بياناتها أو إيقافها عن العمل عند انتهاء وثائقها.',
    shows: [
        'بطاقات علوية: إجمالي المركبات، والمرفوضة، وقيد المراجعة، والموافق عليها',
        'جدول بأعمدة: اسم السائق، النوع، الموديل، السنة، اللوحة، المقاعد، الدرجة، التأمين، الفحص',
        'عمود الدرجة يوضح إن كانت المركبة اقتصادية أو قياسية أو فاخرة',
        'عمود «مركبة اساسية» بنجمة تدل على المركبة الأساسية للسائق',
        'فلتر «كل السائقين» لعرض مركبات سائق بعينه، مع حقل بحث فوري'
    ],
    actions: [
        'اضغط زر الإضافة لتسجيل مركبة جديدة بتحديد السائق والنوع والموديل والسنة واللوحة وعدد المقاعد',
        'حدد درجة المركبة عند الإضافة أو التعديل لتظهر في الحجوزات المناسبة لها',
        'أرفق صورتي التأمين والفحص وحدد تاريخي انتهائهما',
        'اختر «تعديل» لتحديث بيانات مركبة قائمة أو استبدال وثائقها',
        'اختر «تعليق» وأدخل السبب لإيقاف المركبة، أو «إلغاء التعليق» لإعادتها للعمل',
        'اختر «حذف» لإزالة المركبة نهائياً بعد التأكيد',
        'اضغط زر التحديث لإعادة تحميل القائمة بعد أي تغيير'
    ],
    notes: [
        'زرّا الطباعة والتنزيل في شريط الأدوات غير مفعّلين في هذه الصفحة',
        'حسابات المدارس ترى مركبات مدرستها فقط'
    ],
    related: [
        { path: '/SuspendedVehicles', label: 'المركبات الموقوفة' },
        { path: '/Drivers', label: 'السائقون' },
        { path: '/VehicleTypes', label: 'أنواع المركبات' }
    ]
},

'/SuspendedVehicles': {
    tagline: 'المركبات الموقوفة عن العمل وإعادتها للخدمة',
    what: 'صفحة تعرض المركبات المعلّقة فقط، أي التي أُوقفت عن تنفيذ الرحلات لسبب ما مثل انتهاء التأمين أو الفحص الفني. توجد لتجميع هذه الحالات في مكان واحد بدل البحث عنها داخل قائمة المركبات الكاملة. يستخدمها المسؤول لمراجعة أوضاعها وإعادة تشغيل ما استوفى شروطه.',
    shows: [
        'بطاقات علوية: إجمالي المركبات، ومن لديها رحلات، ومن لديها تأمين منتهٍ، ومن لديها فحص فني منتهٍ',
        'جدول بأعمدة: اسم السائق، النوع، الموديل، السنة، اللوحة، المقاعد، الدرجة، التأمين، الفحص',
        'عمود الدرجة يوضح إن كانت المركبة اقتصادية أو قياسية أو فاخرة',
        'عمود «مركبة اساسية» بنجمة تدل على المركبة الأساسية للسائق',
        'حقل بحث فوري لتصفية القائمة، مع أشرطة تنقل بين الصفحات'
    ],
    actions: [
        'راجع تاريخي التأمين والفحص للتأكد من سبب الإيقاف قبل أي إجراء',
        'اختر «إلغاء التعليق» لإعادة المركبة إلى الخدمة بعد التأكيد',
        'ابحث باسم السائق أو رقم اللوحة للوصول السريع إلى مركبة بعينها'
    ],
    notes: [
        'القائمة تُظهر المركبات الموقوفة فعلياً فقط، لذلك يكون «إلغاء التعليق» هو الإجراء المتاح عملياً',
        'تعديل بيانات المركبة أو إضافتها يتم من صفحة المركبات'
    ],
    related: [
        { path: '/Vehicles', label: 'المركبات' },
        { path: '/SuspendedDrivers', label: 'السائقون الموقوفون' }
    ]
},

'/Passengers': {
        tagline: 'سجل حسابات الركّاب والطلاب مع مواقعهم المحفوظة',
        what: 'صفحة إدارة حسابات الركّاب في تطبيق رفيق، وتشمل نوعين: راكب عادي وطالب مرتبط بمدرسة. تعرض بطاقات إحصائية أعلى الشاشة ثم جدولًا قابلًا للبحث والفلترة، ومن كل صف يمكن فتح مواقع الراكب المحفوظة أو تعديل بياناته أو تعليق حسابه.',
        shows: [
            'أربع بطاقات: إجمالي الركاب، الركّاب، الطلاب، أولياء الأمور',
            'جدول فيه صورة الراكب واسمه ورقم هاتفه ونوعه ومدرسته',
            'عمودان لعدد رحلات الراكب وتقييمه',
            'أيقونة معلومات تُظهر نقاط الراكب والملاحظات المسجّلة عليه',
            'حقل بحث وفلتر بالنوع (راكب/طالب) وفلتر بالمدرسة',
            'شاشة مواقع الراكب: اسم الموقع وخط العرض والطول ورابط مباشر وعلامة الموقع الرئيسي'
        ],
        actions: [
            'إضافة راكب جديد بالاسم والهاتف والنوع، والمدرسة إن كان طالبًا، مع وصف اختياري',
            'تعديل بيانات الراكب من نافذة التعديل',
            'عرض المواقع لإضافة موقع محفوظ أو تعديله أو حذفه وتحديد الموقع الرئيسي',
            'تعليق الحساب بعد كتابة سبب التعليق، أو إلغاء التعليق عن حساب معلَّق',
            'حذف الراكب بعد رسالة تأكيد',
            'تحديث القائمة أو تصفير البحث لإعادة عرض كل السجلات'
        ],
        notes: [
            'الصفحة متاحة للمشرف العام ولمالك المدرسة، ومالك المدرسة يرى ركّاب مدرسته فقط',
            'الحسابات المعلَّقة تبقى ظاهرة هنا ولها صفحة مستقلة تجمعها'
        ],
        related: [
            { path: '/SuspendedPassengers', label: 'الركاب الموقوفون' },
            { path: '/Parents', label: 'أولياء الأمور' },
            { path: '/Students', label: 'الطلاب' }
        ]
    },

    '/SuspendedPassengers': {
        tagline: 'قائمة الركّاب المعلَّقين وإعادة تفعيل حساباتهم',
        what: 'نسخة مخصّصة من شاشة الركّاب تعرض الحسابات المعلَّقة فقط. الغرض منها مراجعة من تم إيقافه وإعادة تفعيله عند انتهاء سبب الإيقاف، لذلك لا تحتوي على أزرار الإضافة أو التعديل أو الحذف.',
        shows: [
            'أربع بطاقات: إجمالي الركاب، الركّاب، الطلاب، أولياء الأمور',
            'جدول بصورة الراكب واسمه ورقم هاتفه ونوعه ومدرسته',
            'عدد رحلات الراكب وتقييمه لكل صف',
            'أيقونة معلومات تُظهر النقاط والملاحظات المسجّلة',
            'حقل بحث وفلتر بالنوع وفلتر بالمدرسة'
        ],
        actions: [
            'إلغاء التعليق عن الحساب بعد رسالة تأكيد ليعود الراكب للعمل',
            'تعليق حساب بكتابة سبب التعليق في نافذة مخصّصة',
            'البحث والفلترة بالنوع أو المدرسة لتضييق القائمة',
            'تحديث القائمة لإظهار آخر التغييرات'
        ],
        notes: [
            'الصفحة متاحة للمشرف العام ولمالك المدرسة، ومالك المدرسة يرى مدرسته فقط',
            'إضافة الركّاب وتعديلهم وحذفهم يتم من شاشة الركّاب وليس من هنا'
        ],
        related: [
            { path: '/Passengers', label: 'الركاب' },
            { path: '/UsersSuspend', label: 'الحسابات الموقوفة' }
        ]
    },

    '/Parents': {
        tagline: 'حسابات أولياء الأمور وربطهم بأبنائهم الطلاب',
        what: 'صفحة إدارة أولياء الأمور: من هم، بأي مدرسة يرتبطون، وهل لديهم حساب في التطبيق. أهم ما فيها هو ربط ولي الأمر بأبنائه الطلاب، إضافة إلى تعديل بياناته وتعليق حسابه عند الحاجة.',
        shows: [
            'أربع بطاقات: إجمالي أولياء الأمور، مرتبط بمدرسة، مرتبط بحساب، بدون مدرسة',
            'جدول بصورة ولي الأمر واسمه ورقم هاتفه ومدرسته وعدد الرحلات',
            'حالة الحساب: نشط أو غير نشط أو معلق أو غير مرتبط بحساب',
            'حقل بحث وفلتر بالمدرسة وفلتر بالحالة',
            'نافذة الأبناء تعرض كل طالب مرتبط باسمه وصورته ومدرسته',
            'قائمة الطلاب عند الربط تُظهر لكل طالب: بلا ولي أمر أو ولي أمر واحد أو مكتمل'
        ],
        actions: [
            'إضافة ولي أمر تبدأ بالبحث برقم الهاتف، فإن وُجد تُستكمل بياناته وإلا يُنشأ جديد',
            'تحديد الأبناء أثناء الإضافة وربطهم بولي الأمر في خطوة واحدة',
            'تعديل البيانات: الاسم الأول واسم الأب واللقب والهاتف والمدرسة والملاحظات',
            'عرض الأبناء لمعرفة الطلاب المرتبطين بولي الأمر حاليًا',
            'تعيين أبناء بالبحث عن الطلاب وفلترتهم بالمدرسة ثم تحديدهم دفعة واحدة',
            'تعليق الحساب بكتابة سبب إلزامي، أو إلغاء التعليق عن حساب معلَّق',
            'حذف ولي الأمر بعد رسالة تأكيد'
        ],
        notes: [
            'لا يمكن ربط الطالب بأكثر من ولي أمرين، ويُعطَّل اختياره عند اكتمال العدد',
            'مالك المدرسة وموظفها يريان أولياء أمور مدرستهما فقط'
        ],
        related: [
            { path: '/Students', label: 'الطلاب' },
            { path: '/Passengers', label: 'الركاب' },
            { path: '/SchoolTrips', label: 'المسارات المدرسية' }
        ]
    },

    '/Students': {
        tagline: 'بيانات الطلاب ومدارسهم وعناوينهم على الخريطة',
        what: 'صفحة إدارة الطلاب المسجّلين في النظام: بياناتهم الشخصية ورقم القيد والسنة الدراسية والمدرسة وعنوان السكن المحدَّد على الخريطة. من هنا يُضاف الطالب ويُعدَّل ويُعلَّق حسابه أو يُفعَّل، ويمكن فتح ملف مختصر لكل طالب.',
        shows: [
            'أربع بطاقات: إجمالي الطلاب، مرتبط بمدرسة، مرتبط بحساب، بدون مدرسة',
            'جدول بصورة الطالب واسمه ورقم هاتفه ومدرسته وعدد رحلاته',
            'حالة الحساب: نشط أو غير نشط أو معلق أو غير مرتبط بحساب',
            'حقل بحث وفلتر بالمدرسة وفلتر بالحالة',
            'ملف الطالب يعرض المدرسة وعدد الرحلات والتقييم والاسم الثلاثي والملاحظات',
            'تلميح يوضّح من أنشأ السجل وتاريخ إنشائه'
        ],
        actions: [
            'إضافة طالب بالاسم والهاتف ورقم القيد والسنة الدراسية والمدرسة والملاحظات',
            'تحديد عنوان الطالب وموقعه على الخريطة داخل نافذة الإضافة أو التعديل',
            'تعديل بيانات الطالب من نافذة التعديل',
            'تعليق الحساب بكتابة سبب إلزامي، أو إلغاء التعليق عن حساب معلَّق',
            'تفعيل الحساب أو إيقافه مباشرة من قائمة الإعدادات',
            'حذف الطالب بعد رسالة تأكيد',
            'فتح ملف الطالب لاستعراض بياناته في صفحة مستقلة'
        ],
        notes: [
            'مالك المدرسة وموظفها يريان طلاب مدرستهما فقط',
            'تبويب الرحلات داخل ملف الطالب ما زال بلا بيانات، والرحلات تُتابَع من شاشات الرحلات'
        ],
        related: [
            { path: '/Parents', label: 'أولياء الأمور' },
            { path: '/SchoolTrips', label: 'المسارات المدرسية' },
            { path: '/SchoolAttendanceReport', label: 'تقرير الحضور والغياب' }
        ]
    },

    '/Schools': {
        tagline: 'ملف كل مدرسة: بياناتها ومستخدموها واشتراكها ومحفظتها',
        what: 'المرجع الرئيسي للمدارس المتعاقدة مع رفيق. تعرض قائمة المدارس مع عدد طلابها واشتراكاتها وحالتها، وتفتح لكل مدرسة ثلاث شاشات فرعية: مستخدمو المدرسة، اشتراكاتها، ومحفظتها المالية. كما تتيح إضافة مدرسة جديدة وتحديد قيمة اشتراكها الشهري لكل طالب.',
        shows: [
            'أربع بطاقات: إجمالي المدارس، الطلبة، المدارس لديها اشتراك، الاشتراكات الفعّالة',
            'جدول بصورة المدرسة واسمها وهاتفها وبريدها وإجمالي الطلبة وإجمالي الاشتراكات وحالتها',
            'حقل بحث وفلتر بالمنطقة',
            'شاشة الاشتراك: نوع الاشتراك وتاريخ البداية والنهاية والقيمة والسعر وطريقة الدفع والحالة',
            'شاشة المحفظة: عدّادات المعاملات والمشتريات والتتبع مع ثلاثة جداول تفصيلية',
            'شاشة مستخدمي المدرسة: الاسم والهاتف والبريد وحالة كل مستخدم'
        ],
        actions: [
            'إضافة مدرسة: الاسم والهاتف والبريد والمدينة والبلدية والمنطقة والعنوان التفصيلي وصورة الغلاف',
            'تحديد موقع المدرسة على الخريطة وقيمة الاشتراك الشهري لكل طالب، والموقع إلزامي',
            'تعديل بيانات المدرسة، وتفعيلها أو تعليقها مع كتابة سبب التعليق',
            'عرض الاشتراك لإضافة اشتراك جديد أو إضافة قيمة له أو تسجيل انسحاب أو حذفه',
            'عرض البيانات المالية لشحن محفظة المدرسة بقيمة وطريقة دفع ومتابعة حركاتها',
            'مستخدمو المدرسة: ربط مستخدم قائم بالمدرسة، أو تعليقه وتفعيله، أو إلغاء ربطه',
            'حذف المدرسة نهائيًا، وهو متاح فقط للمدارس الفارغة تمامًا'
        ],
        notes: [
            'الإضافة والتفعيل والتعليق والحذف النهائي متاحة للمشرف العام فقط',
            'مالك المدرسة يفتح الصفحة فيرى مدرسته وحدها',
            'المدرسة الفعّالة تُوقف بالتعليق لا بالحذف، والحذف يُرفض إن كان لها طلاب أو رحلات أو فواتير'
        ],
        related: [
            { path: '/SchoolsUsers', label: 'مسؤولو المدارس' },
            { path: '/SchoolInvoices', label: 'فواتير المدارس' },
            { path: '/Students', label: 'الطلاب' }
        ]
    },

    '/SchoolsUsers': {
        tagline: 'حسابات موظفي المدارس وصلاحياتهم وحالتها',
        what: 'صفحة إدارة الأشخاص الذين يدخلون لوحة التحكم نيابةً عن المدارس، أي مالك المدرسة وموظفوها. تعرض حساباتهم وأي مدرسة يتبع كل حساب، وتتيح إنشاء حساب موظف جديد وربطه بمدرسته مباشرة، إضافة إلى تهيئة كلمة المرور وإيقاف الحساب.',
        shows: [
            'أربع بطاقات: عدد المستخدمين، مدير مدرسة، موظف مدرسة، عدد المدارس',
            'جدول بصورة المستخدم واسمه وهاتفه وبريده الإلكتروني',
            'المدرسة التابع لها كل حساب، أو تنبيه بأنه غير مرتبط بمدرسة',
            'نوع الحساب: مالك المدرسة أو موظف مدرسة، وحالته: نشط أو غير نشط أو معلّق',
            'حقل بحث وفلتر بالمدرسة، وتلميح يعرض منشئ الحساب وتاريخ آخر دخول',
            'نافذة البيانات المالية تعرض بند العملية ونوعها وطريقة الدفع وقيمتها وتاريخها'
        ],
        actions: [
            'إضافة مستخدم بالاسم والهاتف والبريد وصورة الغلاف، مع اختيار المدرسة ونوع الحساب',
            'تعديل بيانات الحساب من نافذة التعديل',
            'تهيئة كلمة المرور للحساب بعد رسالة تأكيد',
            'تفعيل الحساب أو إيقافه، وإلغاء التعليق عن حساب معلَّق',
            'تسليم قيمة مالية للمستخدم وتسجيلها في بياناته المالية',
            'عرض البيانات المالية وحذف أي قيد منها',
            'حذف الحساب بعد رسالة تأكيد'
        ],
        notes: [
            'القائمة تقتصر على العاملين في المدارس ولا تعرض حسابات الإدارة العامة',
            'موظف المدرسة الجديد يُربط بالمدرسة المختارة لحظة الحفظ',
            'عند دخول مالك المدرسة تُخفى قائمة اختيار المدرسة ويُنشأ الحساب على مدرسته'
        ],
        related: [
            { path: '/Schools', label: 'قائمة المدارس' },
            { path: '/Users', label: 'المستخدمون والصلاحيات' }
        ]
    },

    '/SchoolAttendanceReport': {
        tagline: 'تقرير غياب الطلاب لكل مدرسة خلال فترة محددة',
        what: 'شاشة عرض فقط تستعرض حالات غياب الطلاب في الرحلات المدرسية. تختار المدرسة والفترة الزمنية فتظهر قائمة الغياب مع ملخّص سريع بعدد الحالات والطلاب والأيام، ويمكن تصدير النتيجة إلى ملف جدولي.',
        shows: [
            'ثلاثة مؤشرات: إجمالي الغياب، عدد الطلاب، عدد الأيام',
            'حقول تحديد المدرسة ومن تاريخ وإلى تاريخ',
            'جدول بتاريخ الغياب واسم الطالب وصفه واسم ولي أمره',
            'الرحلة أو المسار المرتبط بحالة الغياب وسببها المسجَّل',
            'علامة غائب على كل صف في عمود الحالة'
        ],
        actions: [
            'تحديد المدرسة والفترة الزمنية لتحميل بيانات الغياب فورًا',
            'تصدير CSV لحفظ القائمة الظاهرة في ملف جدولي',
            'تحديث لإعادة جلب البيانات بأحدث حالة'
        ],
        notes: [
            'الصفحة للعرض فقط ولا يُسجَّل الغياب منها',
            'موظفو المدارس يرون بيانات مدرستهم وحدها'
        ],
        related: [
            { path: '/Students', label: 'الطلاب' },
            { path: '/SchoolTrips', label: 'المسارات المدرسية' },
            { path: '/SchoolTripsLive', label: 'الرحلات المدرسية الآن' }
        ]
    },

    '/SchoolInvoices': {
        tagline: 'فواتير المدارس الشهرية وتوليد فاتورة الشهر الحالي',
        what: 'شاشة محاسبية تعرض ما على كل مدرسة من فواتير شهرية واشتراكات خلال فترة تختارها. تحسب الفاتورة الشهرية على أساس عدد الطلاب النشطين وقيمة الاشتراك الشهري لكل طالب، ويمكن من هنا توليد فاتورة الشهر الجاري أو تحديثها.',
        shows: [
            'حقول اختيار المدرسة ومن شهر وإلى شهر',
            'جدول بنوع السجل: فاتورة شهرية أو اشتراك، ورقم الفاتورة واسم المدرسة',
            'الشهر المحاسبي وعدد الطلاب والقيمة الإجمالية بالدينار الليبي',
            'حالة الفاتورة: مستحقة أو مدفوعة أو ملغاة، وحالات الاشتراك: فعّال أو منتهٍ أو موقوف',
            'تنبيه واضح إذا لم يُحدَّد اشتراك شهري لكل طالب في بيانات المدرسة'
        ],
        actions: [
            'اختيار المدرسة والفترة لعرض فواتيرها',
            'توليد فاتورة الشهر الحالي أو تحديثها إن كانت موجودة',
            'تحديث القائمة لإعادة جلب أحدث الفواتير'
        ],
        notes: [
            'الصفحة متاحة للمشرف العام فقط',
            'من دون تحديد الاشتراك الشهري لكل طالب في بيانات المدرسة لن تتولّد أي فاتورة',
            'زر تنزيل الفاتورة بصيغة PDF غير مفعَّل ما لم يوفّر النظام رابط تنزيل للفاتورة'
        ],
        related: [
            { path: '/Schools', label: 'قائمة المدارس' },
            { path: '/FinancialSubscriptions', label: 'سجل حركات الاشتراكات' }
        ]
    },

'/RevenueDashboard': {
        tagline: 'لوحة إيرادات النظام بالأرقام والرسوم خلال فترة تختارها',
        what: 'صفحة تقارير مالية للعرض فقط تجمع إيرادات النظام في مكان واحد. تعرض أربع بطاقات مؤشرات سريعة وثلاثة رسوم بيانية: اتجاه الإيراد اليومي، وتوزيعه على فئات الخدمة، وتوزيعه على طرق الدفع. كل الأرقام تتبع الفترة الزمنية التي تحددها في أعلى الصفحة.',
        shows: [
            'أربع بطاقات: إيرادات اليوم، إيرادات الشهر، عدد الرحلات هذا الشهر، متوسط قيمة الرحلة',
            'نسبة تغيّر أسفل كل بطاقة مقارنة بالأمس أو بالشهر السابق مع سهم صعود أو هبوط',
            'رسم بياني للإيرادات اليومية في آخر ٣٠ يومًا يقارن الفترة الحالية بخط الفترة السابقة',
            'رسم أعمدة للإيرادات حسب فئة الخدمة، تحته قائمة بقيمة كل فئة بالدينار',
            'رسم دائري للإيرادات حسب طريقة الدفع، تحته قيمة كل قناة ونسبتها من الإجمالي',
            'سطر في أسفل البطاقة يبيّن وقت آخر تحديث والفترة المعروضة'
        ],
        actions: [
            'تحديد الفترة بحقلي «من تاريخ» و«إلى تاريخ»، وتتحدّث الصفحة فور التغيير',
            'اختصارات جاهزة للفترة: «اليوم» و«٧ أيام» و«٣٠ يومًا» و«هذا الشهر»',
            'زر «تحديث» لإعادة جلب كل الأرقام والرسوم من جديد',
            'تمرير المؤشر فوق الرسوم لإظهار القيمة الدقيقة لكل يوم أو فئة أو قناة دفع'
        ],
        notes: [
            'صفحة عرض فقط: لا تنشئ ولا تعدّل أي مبلغ.',
            'عند تعذّر التحميل تظهر رسالة خطأ حمراء وتبقى الأرقام أصفارًا بدل عرض أرقام غير حقيقية.',
            'أسماء الفئات وطرق الدفع تأتي من النظام، فتتغيّر القوائم تبعًا لما هو مفعّل فعلاً.'
        ],
        related: [
            { path: '/WalletsDashboard', label: 'أرصدة المحافظ' },
            { path: '/SubscriptionsDashboard', label: 'الاشتراكات — نظرة عامة' }
        ]
    },

    '/WalletsDashboard': {
        tagline: 'لقطة لرصيد كل محفظة مستخدم وإجمالي السيولة',
        what: 'تعرض هذه الصفحة الرصيد الحالي لمحافظ المستخدمين في جدول واحد، مع إجمالي ما بداخل كل المحافظ. هي صورة لحظية للأرصدة وليست كشف حركات، فلا تبيّن من أين جاء المبلغ ولا أين صُرف.',
        shows: [
            'بطاقة «عدد المحافظ» بإجمالي المحافظ النشطة',
            'بطاقة «إجمالي السيولة» بمجموع أرصدة كل المحافظ بالدينار',
            'جدول فيه اسم المستخدم ورقم هاتفه والرصيد الحالي وتاريخ إنشاء المحفظة',
            'عمود «الحركات» فيه زر ينقلك إلى كشف حركات المحافظ',
            'عدّاد أسفل الجدول بعدد السجلات مع أدوات ترقيم الصفحات'
        ],
        actions: [
            'البحث بالاسم أو رقم الهاتف، وتُحدَّث النتائج أثناء الكتابة',
            'زر «تحديث» يمسح البحث ويعيد تحميل القائمة من أولها',
            'تغيير عدد السجلات المعروضة والتنقل بين الصفحات',
            'فتح «الحركات» لمتابعة تفاصيل العمليات على المحافظ'
        ],
        notes: [
            'الأرصدة هنا للاطلاع فقط ولا يمكن تعديلها من هذه الصفحة.'
        ],
        related: [
            { path: '/FinancialTracker', label: 'حركات المحافظ (قبل/بعد)' },
            { path: '/FinancialRecharge', label: 'شحن المحافظ (وارد)' }
        ]
    },

    '/FinancialTracker': {
        tagline: 'كشف حركات المحافظ مع الرصيد قبل كل حركة وبعدها',
        what: 'سجل تفصيلي لكل حركة تمر على محافظ المستخدمين، سواء كانت دفعًا أو إرجاع قيمة أو شحنًا. ميزتها أنها تعرض الرصيد قبل الحركة والرصيد بعدها، فتكشف أي فرق أو تلاعب. الصفحة للمتابعة والتدقيق فقط.',
        shows: [
            'أربع بطاقات: عدد الحركات، قيمة الشحن، عدد العمليات التي تتجاوز ٥٠، وتلك التي تتجاوز ١٠٠',
            'جدول يعرض صاحب المحفظة بصورته ورقم هاتفه',
            'وسم لنوع العملية (دفع أو إرجاع قيمة أو شحن) ووسم لقناة تنفيذها',
            'قناة العملية: لوحة التحكم أو بوابة الويب أو تطبيق الجوال',
            'أعمدة القيمة و«قبل» و«بعد» وتاريخ العملية واسم منفذها',
            'زر معلومات في عمود «أخرى» يُظهر وصف الحركة كاملاً عند مرور المؤشر'
        ],
        actions: [
            'قائمة «فلترة» لاختيار الفلتر: نوع العملية أو قناة العملية',
            'تحديد نوع العملية من قائمة تشمل الكل أو الدفع أو إرجاع القيمة أو الشحن',
            'تحديد قناة العملية: الكل أو لوحة التحكم أو بوابة الويب أو تطبيق الجوال',
            'البحث بالاسم أو رقم الهاتف',
            'تحديد فترة بحقلي «من» و«إلى»',
            'زر «تحديث» يلغي كل الفلاتر والتواريخ ويعيد عرض القائمة كاملة'
        ],
        notes: [
            'صفحة عرض فقط: لا إضافة ولا تعديل ولا حذف لأي حركة.',
            'عنوان عمود صاحب المحفظة مكتوب «الطالب» كبقايا قالب قديم، والمقصود مالك المحفظة.',
            'وسوم النوع والقناة تظهر مختصرة بالإنجليزية في الجدول وهي نفس القيم الموضّحة هنا.'
        ],
        related: [
            { path: '/WalletsDashboard', label: 'أرصدة المحافظ' },
            { path: '/FinancialRecharge', label: 'شحن المحافظ (وارد)' },
            { path: '/WalletPurchases', label: 'مشتريات المحافظ (صادر)' }
        ]
    },

    '/FinancialRecharge': {
        tagline: 'سجل الأموال الداخلة إلى المحافظ وطريقة دفع كل عملية',
        what: 'تعرض عمليات شحن المحافظ، أي الأموال التي دخلت إلى رصيد المستخدمين. كل سطر يبيّن صاحب المحفظة وقيمة الشحن وطريقة الدفع ومن نفّذ العملية ومتى. الاتجاه هنا وارد فقط، أما ما يُخصم من المحافظ فله صفحة أخرى.',
        shows: [
            'أربع بطاقات: عدد الحركات، قيمة الشحن، عدد العمليات التي تتجاوز ٥٠، وتلك التي تتجاوز ١٠٠',
            'شارة في الشريط العلوي بعدد السجلات المطابقة للفلترة الحالية',
            'جدول يعرض صاحب المحفظة بصورته ورقم هاتفه',
            'أعمدة قيمة الشحن وطريقة الدفع وتاريخ العملية واسم منفذها',
            'وسم أخضر «شحن» في عمود نوع العملية لكل السطور'
        ],
        actions: [
            'قائمة «فلترة» لاختيار الفلتر: المستخدم أو الموزع أو طريقة الدفع',
            'اختيار قيمة الفلتر من قائمة منسدلة، وتُحدَّث النتائج فورًا',
            'البحث بالاسم أو رقم الهاتف',
            'تحديد فترة بحقلي «من» و«إلى»',
            'زر «تحديث» يلغي كل الفلاتر والتواريخ ويعيد التحميل',
            'التنقل بين الصفحات من أسفل الجدول'
        ],
        notes: [
            'صفحة عرض فقط: لا يمكن شحن محفظة أو تعديل عملية من هنا.',
            'شحن أرصدة المستخدمين في رفيق يتم عبر كروت الشحن، ولها صفحاتها المستقلة.'
        ],
        related: [
            { path: '/WalletPurchases', label: 'مشتريات المحافظ (صادر)' },
            { path: '/Charging', label: 'عمليات الشحن الناجحة' },
            { path: '/FinancialTracker', label: 'حركات المحافظ (قبل/بعد)' }
        ]
    },

    '/WalletPurchases': {
        tagline: 'سجل الأموال الخارجة من المحافظ مقابل رحلات أو اشتراكات',
        what: 'تعرض ما صُرف من محافظ المستخدمين: قيمة كل عملية، وهل كانت مقابل رحلة أم مقابل اشتراك. هي الوجه المقابل لصفحة شحن المحافظ، فالاتجاه هنا خصم من الرصيد وليس إضافة إليه.',
        shows: [
            'خمس بطاقات: عدد العمليات، إجمالي القيمة، إجمالي سعر الدورات، عمليات الاشتراك، عمليات الرحلات',
            'جدول يعرض مالك المحفظة ورقم هاتفه',
            'وسم لنوع العملية: «اشتراك» أو «رحلة»',
            'أعمدة اسم الاشتراك والقيمة المخصومة وسعر الدورة',
            'تاريخ العملية واسم من نفّذها',
            'عدّاد بعدد السجلات مع ترقيم الصفحات أسفل الجدول'
        ],
        actions: [
            'البحث بالاسم أو رقم الهاتف أو نوع الاشتراك',
            'تفعيل خيار «اشتراكات فقط» لإخفاء عمليات الرحلات',
            'حصر النتائج على محفظة واحدة بكتابة رقمها في حقل رقم المحفظة',
            'اختيار مستخدم معيّن من قائمة المستخدمين',
            'تحديد فترة بحقلي «من» و«إلى»',
            'زر «تحديث» يعيد كل الفلاتر إلى وضعها الأول ويحمّل القائمة من جديد'
        ],
        notes: [
            'صفحة عرض فقط: لا يمكن استرجاع مبلغ أو تعديل عملية من هنا.',
            'حقل رقم المحفظة مكتوب عنوانه بالإنجليزية في الواجهة والمقصود رقم المحفظة.'
        ],
        related: [
            { path: '/FinancialRecharge', label: 'شحن المحافظ (وارد)' },
            { path: '/FinancialSubscriptions', label: 'سجل حركات الاشتراكات' },
            { path: '/FinancialTracker', label: 'حركات المحافظ (قبل/بعد)' }
        ]
    },

    '/Financial': {
        tagline: 'اعتماد طلبات سحب الأرباح وتسجيل القيود المالية اليدوية',
        what: 'صفحة واحدة بتبويبين فوق السجل المالي نفسه. التبويب الأول يخص طلبات سحب أرباح السائقين فتقبلها أو ترفضها، والتبويب الثاني يخص القيود المالية اليدوية فتضيفها أو تعدّلها. الرابط القديم لطلبات السحب يفتح التبويب الأول مباشرة.',
        shows: [
            'تبويبان أعلى الصفحة: «طلبات السحب — اعتماد / رفض» و«القيود المالية اليدوية»',
            'في تبويب السحب: جدول بصاحب الطلب ونوع العملية (سحب أو إيداع) وطريقة الدفع والقيمة',
            'عمود «إجراءات السحب» فيه زرا القبول والرفض، أو وسم معتمدة أو مرفوضة أو قيد المراجعة',
            'في تبويب القيود: بطاقات إجمالي قيمة المحافظ وعدد الحركات وقيمة السحب وقيمة الإيداع والعمليات المحذوفة',
            'جدول القيود يعرض المستفيد ونوع حسابه والبند ونوع العملية وطريقة الدفع والقيمة ووصفها',
            'زر معلومات يُظهر منفذ العملية وتاريخها عند مرور المؤشر'
        ],
        actions: [
            'زر «قبول» ثم «تأكيد الاعتماد» لاعتماد طلب سحب معلّق وتحويل قيمته نهائيًا',
            'زر «رفض» مع كتابة سبب اختياري ثم «تأكيد الرفض»؛ يعود المبلغ للمحفظة ويصل إشعار لصاحبه',
            'زر «إضافة حركة» لتسجيل قيد يدوي: بند العملية والقيمة إلزاميان، وطريقة الدفع والوصف اختياريان',
            'أيقونة التعديل في عمود «الإعدادات» لفتح قيد قائم وتعديل بياناته',
            'الفلترة حسب طريقة الدفع أو بند العملية أو نوع العملية (سحب أو إيداع)',
            'البحث بالاسم أو رقم الهاتف وتحديد فترة بحقلي «من» و«إلى»'
        ],
        notes: [
            'زرا القبول والرفض يظهران فقط لطلبات سحب أرباح السائقين التي ما زالت قيد المراجعة.',
            'القيد اليدوي يُسجَّل في السجل المالي ولا يحرّك رصيد أي محفظة.'
        ],
        related: [
            { path: '/FinancialTracker', label: 'حركات المحافظ (قبل/بعد)' },
            { path: '/WalletsDashboard', label: 'أرصدة المحافظ' }
        ]
    },

    '/SubscriptionsDashboard': {
        tagline: 'ملخّص حالة الاشتراكات وإيرادها الشهري المتكرر',
        what: 'لوحة موجزة للاشتراكات: كم اشتراكًا فعّالاً لديك الآن، وكم يقدَّر دخلها الشهري المتكرر، وكم اشتراكًا قارب على الانتهاء. تحتها جدول بالاشتراكات المقترب موعد انتهائها وتوزيع الاشتراكات على الباقات والحالات.',
        shows: [
            'بطاقة «الاشتراكات النشطة» بعدد الاشتراكات الفعّالة حاليًا',
            'بطاقة «الإيراد الشهري التقريبي» بالقيمة المتكررة للاشتراكات النشطة بالدينار',
            'بطاقة «تنتهي قريباً» بعدد الاشتراكات التي قاربت على الانتهاء',
            'جدول «اشتراكات تنتهي قريباً» فيه الاسم والباقة وتاريخ الانتهاء',
            'قائمة «التوزيع حسب نوع الباقة» بأعمدة نسبية تبيّن عدد الاشتراكات لكل باقة',
            'قسم «التوزيع حسب الحالة»: نشط ومنتهي وموقوف وخارج الخدمة ومحذوف'
        ],
        actions: [
            'زر «تحديث» أعلى الصفحة لإعادة جلب كل الأرقام والقوائم',
            'قراءة تواريخ الانتهاء لتحديد الاشتراكات التي تحتاج تجديدًا قبل انقطاعها'
        ],
        notes: [
            'صفحة عرض فقط: لا يمكن تجديد اشتراك أو إيقافه من هنا.'
        ],
        related: [
            { path: '/FinancialSubscriptions', label: 'سجل حركات الاشتراكات' },
            { path: '/SubscriptionsTypes', label: 'باقات اشتراك السائقين' }
        ]
    },

    '/FinancialSubscriptions': {
        tagline: 'سجل عمليات الاشتراك للمدارس والسائقين بقيمها ومددها',
        what: 'سجل تفصيلي لكل عملية اشتراك: لمن هي، وأي نوع اشتراك، ومن أي تاريخ إلى أي تاريخ، وبأي قيمة، وما حالتها الآن. تُستخدم لمراجعة الاشتراكات الفردية بعد الاطلاع على الملخّص العام.',
        shows: [
            'أربع بطاقات: عدد الحركات، قيمة الإشتراكات، سعر الدورات التدريبية، الدورات المجانية',
            'شارة في الشريط العلوي بعدد السجلات المطابقة للفلترة الحالية',
            'جدول يبدأ باسم المدرسة أو السائق صاحب الاشتراك ونوع الاشتراك',
            'تاريخا بداية الاشتراك ونهايته في عمودي «من» و«إلى»',
            'عمودا القيمة والسعر بالدينار، ثم تاريخ العملية واسم منفذها',
            'وسم للحالة: نشط أو منتهي أو موقوف أو خارج الخدمة'
        ],
        actions: [
            'البحث باسم المدرسة أو السائق أو نوع الاشتراك',
            'تحديد فترة بحقلي «من» و«إلى» لحصر العمليات فيها',
            'زر «تحديث» لإعادة تحميل السجل بالفلاتر الحالية',
            'التنقل بين الصفحات من أسفل الجدول'
        ],
        notes: [
            'صفحة عرض فقط: لا يمكن إنشاء اشتراك أو إلغاؤه من هنا.',
            'أسعار الباقات ومددها تُضبط في صفحة باقات اشتراك السائقين.'
        ],
        related: [
            { path: '/SubscriptionsDashboard', label: 'الاشتراكات — نظرة عامة' },
            { path: '/SubscriptionsTypes', label: 'باقات اشتراك السائقين' },
            { path: '/WalletPurchases', label: 'مشتريات المحافظ (صادر)' }
        ]
    },

'/VoucherCardsDashboard': {
    tagline: 'لوحة أرقام ورسوم بيانية لكروت الشحن',
    what: 'صفحة عرض فقط تلخّص منظومة كروت الشحن كاملة: كم باقة أُنشئت وكم كرتًا تحتويه، وكم منها شُحن فعلًا وكم بقي. تحتها رسوم بيانية أسبوعية وسنوية للمبيعات وقيم الشحن، وثلاث قوائم لأفضل الموزعين.',
    shows: [
        'بطاقتان علويتان: إجمالي عدد الباقات وعدد الباقات المفعلة',
        'بيانات الباقات: عدد الكروت وقيمتها والكروت المشحونة والمتبقية',
        'بيانات الكروت: عدد الكروت وقيمتها وعدد الكروت المشحونة وقيمتها',
        'إجمالي الكروت في النظام وإجمالي قيمتها بالدينار',
        'قيمة المبيعات وعدد الكروت للأسبوع الماضي مع فارق الأسبوع السابق',
        'تقرير سنوي بتبويبين: حركة الكروت وقيمة الكروت، ورسم لآخر ستة أشهر',
        'ثلاث قوائم: أكثر الموزعين مبيعًا، وأكثرهم إدخالًا للأموال، وأعلاهم رصيدًا'
    ],
    actions: [],
    notes: [
        'الصفحة للقراءة فقط؛ الأرقام تُحسب لحظة فتح الصفحة ولا يمكن تعديل شيء منها.'
    ],
    related: [
        { path: '/VoucherCards', label: 'باقات الكروت' },
        { path: '/VoucherCardsDistributors', label: 'الموزعون' },
        { path: '/Charging', label: 'عمليات الشحن الناجحة' }
    ]
},

'/VoucherCards': {
    tagline: 'توليد دفعات كروت وبيعها للموزعين ومتابعتها',
    what: 'هذه الصفحة تدير الباقات أي دفعات الكروت، لا الكروت المفردة. كل صف دفعة واحدة باسمها وموزعها وعدد كروتها وسعر موحّد للكرت. وتمر كل باقة بأربع مراحل: انتظار تأكيد البيع، ثم انتظار فتحها من الموزع، ثم مفعلة، وقد تُجمَّد.',
    shows: [
        'أربع بطاقات: باقة في انتظار البيع، في انتظار الفتح من الموزع، مفعلة، ومجمدة',
        'جدول الباقات: الاسم والموزع وهاتفه، والحالة، والرقم التسلسلي، وسعر الكرت',
        'عدد الكروت في الباقة، ونسبة البيع كدائرة مئوية، وتاريخ الإنشاء بالساعة',
        'حقل بحث نصي وقائمة «كل الموزعين» لقصر العرض على موزّع بعينه',
        'صفحة تفاصيل الباقة: عدد الكروت وسعر الكرت والمشحونة والمتبقية ونسبة الشحن',
        'تاريخ الإصدار وتاريخ الصلاحية بشارة لونها يتغير عند اقتراب الانتهاء',
        'تبويبات التفاصيل: بيانات الباقة، بيانات الموزع، وحركات الباقة بمن نفّذ كل خطوة'
    ],
    actions: [
        'زر الإضافة لإنشاء باقة: اختيار الموزع واسم الباقة وعدد الكروت وقيمة الكرت',
        '«تأكيد البيع» للباقة المنتظرة، وينزل معه ملف الكروت المشفّر للموزع',
        '«تجميد الباقة» لإيقافها، و«إلغاء تجميد الباقة» لإعادة تفعيلها',
        '«تنزيل الكروت (CSV)» للحصول على أرقام كروت الباقة بصيغة مقروءة',
        '«عرض التفاصيل» لفتح صفحة الباقة، و«حذف» لإزالة الباقة نهائيًا',
        '«طباعة القائمة» و«تصدير CSV» لكل الصفوف المطابقة للفلتر، و«تحديث» لمسح الفلاتر'
    ],
    notes: [
        'مفتاح فك تشفير ملف الباقة مخفي في تبويب بيانات الباقة ويظهر بتمرير المؤشر عليه.',
        'ملف «تأكيد البيع» مشفّر ومخصص للموزع، أما «تنزيل الكروت» فيعطي أرقامًا مقروءة.'
    ],
    related: [
        { path: '/Cards', label: 'قائمة الكروت' },
        { path: '/VoucherCardsDistributors', label: 'الموزعون' },
        { path: '/VoucherCardsDashboard', label: 'نظرة عامة على الكروت' }
    ]
},

'/Cards': {
    tagline: 'جدول بكل كرت على حدة مع قيمته وحالته',
    what: 'خلافًا لصفحة الباقات التي تعرض الدفعات مجمّعة، هذه الصفحة تسرد الكروت واحدًا واحدًا مع الباقة التي ينتمي إليها والمرحلة التي وصل إليها. صفحة عرض بحت لا تُنفَّذ منها أي عملية على الكرت.',
    shows: [
        'بطاقتان: إجمالي عدد الكروت وإجمالي قيمتها بالدينار',
        'جدول: الرقم التسلسلي، والقيمة، والحالة أو المستوى، واسم الباقة، وتاريخ الإنشاء',
        'حالات الكرت الست: تم الإنشاء، تم البيع، مفتوح، تم الشحن، مجمّد، منتهي',
        'حقل بحث يقبل الرقم التسلسلي أو اسم الباقة',
        'عدّاد بعدد السجلات الكلي وترقيم صفحات بعشرة سجلات في الصفحة'
    ],
    actions: [
        'البحث بالرقم التسلسلي أو باسم الباقة لتضييق القائمة فورًا',
        'زر التحديث لمسح البحث وإعادة تحميل القائمة من صفحتها الأولى'
    ],
    notes: [
        'للاستعلام عن كرت واحد بتفاصيله الكاملة استخدم صفحة البحث عن كرت.'
    ],
    related: [
        { path: '/VoucherCardsCards', label: 'بحث عن كرت' },
        { path: '/VoucherCards', label: 'باقات الكروت' },
        { path: '/Charging', label: 'عمليات الشحن الناجحة' }
    ]
},

'/VoucherCardsCards': {
    tagline: 'استعلام عن كرت واحد وتتبع مساره كاملًا',
    what: 'صفحة تتبّع كرت بعينه: تُدخل رقمه التسلسلي أو رقمه السري فتظهر بطاقة الكرت وحالته، ومن شحنه ومتى إن كان قد شُحن، والباقة التي جاء منها والموزع الذي باعها. في أعلى الصفحة أرقام عامة عن الكروت.',
    shows: [
        'أربع بطاقات: عدد الكروت وقيمتها الإجمالية، والكروت المعروضة للبيع وقيمتها',
        'ثلاثة أرقام: عدد الكروت المشحونة وقيمتها وعدد محاولات التعبئة الفاشلة',
        'بيانات الكرت: رقم الكرت والرقم التسلسلي والقيمة وتاريخ الإصدار وحالته',
        'بيانات التعبئة عند وجودها: اسم المستخدم وهاتفه والقيمة المضافة وتاريخها والقناة',
        'بيانات الباقة والموزع: اسم الباقة وتسلسلها ومنفّذها وتاريخا البيع والفتح',
        'أعداد كروت الباقة الإجمالية والمشحونة والمتبقية، وباقات الموزع المفعلة والمجمدة'
    ],
    actions: [
        'البحث بالرقم التسلسلي للكرت',
        'البحث بالرقم السري للكرت',
        'زر التحديث لمسح نتيجة البحث وإعادة قراءة الأرقام العامة'
    ],
    notes: [
        'الحقلان يقبلان أرقامًا فقط، ولا تظهر نتيجة إن لم يطابق الرقم أي كرت.',
        'قناة التعبئة تُبيّن مصدر العملية: لوحة التحكم أو الموقع أو التطبيق.'
    ],
    related: [
        { path: '/Cards', label: 'قائمة الكروت' },
        { path: '/Charging', label: 'عمليات الشحن الناجحة' },
        { path: '/VoucherCardsTryAttemp', label: 'محاولات الشحن الفاشلة' }
    ]
},

'/Charging': {
    tagline: 'سجل الكروت التي استُبدلت فعلًا ومن استبدلها',
    what: 'سجل العمليات الناجحة فقط: كل سطر كرت استُبدل بنجاح وأُضيفت قيمته إلى محفظة مستخدم. يُستخدم لمطابقة قيمة الشحن المُعلنة مع ما دخل المحافظ خلال فترة محددة.',
    shows: [
        'بطاقتان: عدد عمليات الشحن وإجمالي قيمتها بالدينار خلال الفلتر الحالي',
        'جدول: الرقم التسلسلي للكرت، والقيمة، واسم المستخدم ورقم هاتفه',
        'عمود القناة التي تمت منها العملية، وتاريخ العملية',
        'حقل بحث يقبل الرقم التسلسلي أو اسم المستخدم أو رقم الهاتف',
        'حقلا «من» و«إلى» لحصر السجل في فترة زمنية، وترقيم للصفحات'
    ],
    actions: [
        'البحث بالرقم التسلسلي أو اسم المستخدم أو الهاتف',
        'تحديد فترة زمنية بحقلي «من» و«إلى» فتتحدث البطاقات والجدول معًا',
        'زر التحديث لمسح البحث والتواريخ والعودة للسجل الكامل'
    ],
    notes: [
        'هنا العمليات الناجحة فقط؛ المحاولات المرفوضة لها صفحة مستقلة.'
    ],
    related: [
        { path: '/VoucherCardsTryAttemp', label: 'محاولات الشحن الفاشلة' },
        { path: '/Cards', label: 'قائمة الكروت' },
        { path: '/FinancialRecharge', label: 'شحن المحافظ' }
    ]
},

'/VoucherCardsTryAttemp': {
    tagline: 'سجل محاولات استبدال الكروت المرفوضة وأسبابها',
    what: 'الوجه المقابل لسجل الشحن الناجح: هنا محاولات تعبئة لم تنجح، مع اسم من حاول ورقم الكرت الذي استُخدم وسبب الرفض. تكرار المحاولات من شخص واحد أو على كرت واحد مؤشر على تلاعب.',
    shows: [
        'جدول: اسم المستخدم مع صورته ورقم هاتفه',
        'رقم الكرت الذي جرت عليه المحاولة',
        'سبب رفض المحاولة والتأثير المترتب عليها',
        'حالة التعبئة: نجحت العملية أو لم تنجح عملية التعبئة',
        'تاريخ المحاولة ووقتها، مع حقل بحث وترقيم للصفحات'
    ],
    actions: [
        'البحث في المحاولات بالاسم أو رقم الكرت',
        'زر التحديث لإعادة تحميل السجل'
    ],
    notes: [
        'تكرار الرفض على الكرت نفسه أو من الشخص نفسه يستدعي مراجعة الحساب.'
    ],
    related: [
        { path: '/Charging', label: 'عمليات الشحن الناجحة' },
        { path: '/VoucherCardsCards', label: 'بحث عن كرت' }
    ]
},

'/VoucherCardsDistributors': {
    tagline: 'إدارة موزعي الكروت وحساباتهم ومستحقاتهم',
    what: 'سجل الموزعين الذين تُباع لهم باقات الكروت. يعرض لكل موزع حجم ما استلمه وما بيع منه وما تبقى، ويتيح فتح حساب مالي له لتسجيل ما سدّده من قيمة الكروت.',
    shows: [
        'أربع بطاقات: عدد الموزعين، والنشط، وغير النشط، والمحذوف',
        'جدول: اسم الموزع مع صورته ورقم هاتفه',
        'أعداد الباقات والكروت وقيمتها، وعدد الكروت المبيوعة والمتبقية لكل موزع',
        'حالة الموزع: نشط أو غير نشط',
        'أيقونة الساعة تُظهر منفّذ إنشاء الحساب وتاريخه',
        'نافذة البيانات المالية: البند ونوع العملية سحب أو إيداع وطريقة الدفع والقيمة والوصف'
    ],
    actions: [
        'زر الإضافة لفتح حساب موزع جديد: الاسم والهاتف والبريد والصورة',
        '«تعديل البيانات» لتحديث اسم الموزع أو هاتفه أو بريده أو صورته',
        '«تهيئة كلمة المرور» لإعادة ضبط كلمة مرور دخول الموزع',
        '«تسليم قيمة مالية»: وصف العملية وبندها وطريقة الدفع والقيمة وتاريخها',
        '«عرض البيانات المالية» لاستعراض قيود الموزع وحذف أي قيد منها',
        '«المواقع» لإدارة نقاط بيع الموزع: الاسم والهاتف وساعات العمل وأيام العمل وتحديد الموقع على الخريطة؛ هذه النقاط هي ما يراه الراكب في التطبيق تحت «أماكن بيع كروت الشحن» مرتّبة حسب الأقرب إليه',
        '«تغير حالة العرض» لتفعيل الموزع أو إيقافه، و«حذف» لإزالة حسابه'
    ],
    notes: [
        'قائمتا بند العملية وطريقة الدفع تُقرآن من إعدادات النظام المالية.',
        'الموزع المُوقَف لا يظهر للمستخدمين ولا تُفتح له باقات جديدة، وتختفي نقاط بيعه من التطبيق.',
        'نقطة البيع بلا موقع على الخريطة لا تظهر للركاب؛ وإن تُرك هاتفها فارغاً يُعرض هاتف الموزع.'
    ],
    related: [
        { path: '/VoucherCards', label: 'باقات الكروت' },
        { path: '/VoucherCardsDashboard', label: 'نظرة عامة على الكروت' },
        { path: '/PaymentMethods', label: 'طرق الدفع' }
    ]
},

'/Pricing': {
    tagline: 'ضبط تعريفة كل فئة رحلة ومضاعفات السعر',
    what: 'هذه هي لوحة التسعير الرئيسية. تُختار فئة الرحلة من شريط التبويبات في الأعلى، فتظهر تعريفتها الكاملة: مكوّنات السعر الأساسية ثم ثمانية مضاعِفات ترفع السعر حسب الظرف، إضافة إلى نوافذ زمنية تحدد متى تسري ذروة السعر أو التعرفة الليلية.',
    shows: [
        'شريط تبويبات بأسماء فئات الرحلات لاختيار الفئة المراد تسعيرها',
        'المكوّنات الأساسية: السعر الأساسي وسعر الكيلومتر وسعر الدقيقة وسعر الانتظار',
        'عدد دقائق الانتظار المجانية من صفر إلى ثلاثين، ونسبة رسوم الإلغاء المئوية',
        'ثمانية مضاعِفات: الفئة والازدحام والطقس وشُح السائقين والذروة والمناطق النائية والليل ونوع المركبة',
        'محرر النوافذ الزمنية: نوع النافذة ووقت بدايتها ونهايتها ومضاعِفها وأيام الأسبوع',
        'لوحة «الفوارق النشطة الآن» تبيّن مضاعِفي الذروة والليل الساريين هذه اللحظة'
    ],
    actions: [
        'تعديل قيم الفئة المختارة ثم «حفظ التعديلات»، وتظهر رسالة بعدد الحقول المعدَّلة',
        '«إلغاء التغييرات» للتراجع وإعادة تحميل القيم المحفوظة',
        '«إضافة نافذة ذروة» أو «إضافة نافذة ليلية»، وحذف أي نافذة قائمة',
        '«القيم الافتراضية» لاسترجاع النوافذ الموصى بها، ثم «حفظ النوافذ» لاعتمادها',
        '«سجل التغييرات» للانتقال إلى سجل تعديلات الأسعار',
        '«تحديث» لإعادة قراءة الإعدادات والمضاعِفات النشطة'
    ],
    notes: [
        'كل المضاعِفات قيم موجبة بين ٠٫٠١ و١٠، والقيمة ١ تعني بلا زيادة على السعر.',
        'كل تعديل يُسجَّل باسم من نفّذه وتاريخه في سجل تغييرات الأسعار.'
    ],
    related: [
        { path: '/PricingChangeLog', label: 'سجل تغييرات الأسعار' },
        { path: '/TripCategories', label: 'فئات الرحلات' },
        { path: '/CommissionRules', label: 'قواعد العمولة' }
    ]
},

'/CommissionRules': {
    tagline: 'تحديد نسبة رفيق من كل رحلة حسب ثلاثة أبعاد',
    what: 'العمولة التي يقتطعها رفيق ليست رقمًا واحدًا، بل قواعد بثلاثة أبعاد: فئة الرحلة، ونوع السائق، وشريحة عدد الرحلات. تُطبَّق القاعدة الأكثر تحديدًا، وترك أي بُعد على «الكل» يجعل القاعدة شاملة لذلك البُعد.',
    shows: [
        'جدول القواعد: فئة الرحلة ونوع السائق والشريحة والنسبة المئوية',
        'نوع السائق: موظف لدى رفيق، أو مستقل، أو الكل',
        'الشريحة: ضمن الباقة، أو تجاوز الباقة، أو الكل',
        'نموذج الإضافة والتعديل بقوائم منسدلة بأسماء الفئات لا بأرقامها',
        'رسالة «لا توجد قواعد عمولة فعّالة» حين يخلو الجدول'
    ],
    actions: [
        '«+ قاعدة جديدة» ثم اختيار الفئة ونوع السائق والشريحة وإدخال النسبة',
        '«تعديل» على أي صف لتغيير أبعاده أو نسبته ثم «حفظ التعديل»',
        '«حذف» بعد التأكيد، فتُخفى القاعدة ولا تعود للظهور في القائمة',
        '«تحديث» لإعادة قراءة القواعد الفعّالة'
    ],
    notes: [
        'النسبة يجب أن تكون بين صفر ومئة وإلا رُفض الحفظ برسالة تنبيه.',
        'كلما زاد تحديد القاعدة سبقت القاعدة الأعم عند حساب عمولة الرحلة.'
    ],
    related: [
        { path: '/Pricing', label: 'التسعير الديناميكي' },
        { path: '/TripCategories', label: 'فئات الرحلات' },
        { path: '/RevenueDashboard', label: 'الإيرادات والأرباح' }
    ]
},

'/CancellationPolicy': {
    tagline: 'قواعد رسوم الإلغاء وعدم حضور الراكب',
    what: 'تحدد هذه الصفحة ماذا يُخصم عند إلغاء الرحلة ومن يتحمّله. لكل قاعدة جهة مُلغِية، ومرحلة من مراحل الرحلة، ومهلة بالدقائق قبل الموعد، ثم رسوم بالنسبة المئوية وحد أدنى بالدينار.',
    shows: [
        'جدول القواعد: المُلغِي ومرحلة الرحلة والمهلة بالدقائق',
        'الرسوم بالنسبة المئوية والحد الأدنى بالدينار ووصف القاعدة',
        'الجهة المُلغِية: الراكب أو السائق أو المسؤول أو حالة عدم الحضور',
        'حالة كل قاعدة: مفعّل أو معطّل',
        'نموذج في أسفل الصفحة يُستخدم للإضافة وللتعديل معًا'
    ],
    actions: [
        'تعبئة النموذج ثم «إضافة قاعدة جديدة»',
        '«تعديل» لنقل بيانات القاعدة إلى النموذج ثم «حفظ التعديلات»',
        '«حذف» لإزالة القاعدة بعد التأكيد',
        '«إلغاء» لتفريغ النموذج، و«تحديث» لإعادة قراءة القواعد'
    ],
    notes: [
        'ترك مرحلة الرحلة والمهلة بالدقائق فارغين يجعل القاعدة سارية في كل الحالات.',
        'يُؤخذ الأكبر بين الرسوم بالنسبة والحد الأدنى المحدد بالدينار.'
    ],
    related: [
        { path: '/Pricing', label: 'التسعير الديناميكي' },
        { path: '/Trips', label: 'كل الرحلات' }
    ]
},

'/TripCategories': {
    tagline: 'إدارة فئات الرحلات وتسعيرها الأساسي',
    what: 'قاموس الفئات التي يختار منها الراكب عند الطلب: عادية ومشتركة ومدرسية ونسائية. لكل فئة نوعها وفئة المركبة المؤهلة لها وأسعارها الثلاثة الأساسية، ويمكن إيقاف الفئة فتختفي من التطبيق.',
    shows: [
        'ثلاث بطاقات: العدد الكلي للفئات والنشط منها وغير النشط',
        'جدول: اسم الفئة ووصفها، ونوع الرحلة، وفئة المركبة المطلوبة',
        'سعر البداية وسعر الكيلومتر وسعر الدقيقة لكل فئة بالدينار',
        'أنواع الرحلات المتاحة: عادية، مشتركة، مدرسية، نسائية',
        'فئات المركبات: بدون قيد، عادي، فاخر',
        'حالة الفئة نشطة أو موقوفة'
    ],
    actions: [
        'زر الإضافة لإنشاء فئة: الاسم والنوع وفئة المركبة والأسعار الثلاثة والأيقونة والوصف',
        '«تعديل» لتغيير بيانات فئة قائمة وأسعارها',
        '«تفعيل» أو «إيقاف» الفئة بعد التأكيد',
        'زر التحديث لإعادة قراءة القائمة'
    ],
    notes: [
        'الأسعار المعروضة هنا تعكس إعدادات التسعير، والضبط التفصيلي والمضاعِفات في صفحة التسعير.',
        'إيقاف فئة يخفيها عن الركاب دون حذف بياناتها.'
    ],
    related: [
        { path: '/Pricing', label: 'التسعير الديناميكي' },
        { path: '/CommissionRules', label: 'قواعد العمولة' }
    ]
},

'/SubscriptionsTypes': {
    tagline: 'خطط اشتراك السائقين بأسعارها ومددها',
    what: 'قائمة باقات الاشتراك التي يشتريها السائق من محفظته. لكل باقة اسم وسعر بالدينار ومدة محسوبة بالأشهر ووصف وصورة، ويمكن إيقاف الباقة فلا تظهر للسائقين.',
    shows: [
        'ثلاث بطاقات: عدد الباقات والنشط منها وغير النشط',
        'جدول: صورة الباقة واسمها ووصفها المختصر',
        'سعر الباقة بالدينار ومدتها بالأشهر',
        'حالة الباقة نشطة أو موقوفة',
        'أيقونة الساعة تُظهر منفّذ الإدراج وتاريخه',
        'حقل بحث نصي وترقيم للصفحات'
    ],
    actions: [
        'زر الإضافة لإنشاء باقة: الاسم والسعر والمدة بالأشهر والوصف والصورة',
        '«تعديل» لتغيير سعر الباقة أو مدتها أو بياناتها',
        '«تفعيل» أو «إيقاف» عرض الباقة بعد التأكيد',
        '«حذف» لإزالة الباقة بعد التأكيد',
        'البحث بالاسم وزر التحديث لإعادة تحميل القائمة'
    ],
    notes: [
        'الاسم والسعر والمدة حقول إلزامية، والمدة تُدخل بالأشهر فقط.'
    ],
    related: [
        { path: '/SubscriptionsDashboard', label: 'الاشتراكات — نظرة عامة' },
        { path: '/FinancialSubscriptions', label: 'سجل حركات الاشتراكات' },
        { path: '/Pricing', label: 'التسعير الديناميكي' }
    ]
},

'/PaymentMethods': {
    tagline: 'عرض طرق الدفع المتاحة في النظام',
    what: 'قائمة مرجعية بطرق الدفع التي تستخدمها بقية الصفحات في قوائمها المنسدلة، مثل نموذج تسليم القيمة للموزع والقيود المالية. الصفحة للاطلاع فقط ولا تُدار منها الطرق.',
    shows: [
        'جدول بثلاثة أعمدة: التسلسل واسم طريقة الدفع مع صورتها وحالتها',
        'كل طريقة معروضة بحالة مفعّلة',
        'حقل بحث يصفّي القائمة بالاسم مباشرة أثناء الكتابة'
    ],
    actions: [
        'البحث بالاسم لتصفية قائمة طرق الدفع',
        'زر التحديث لإعادة قراءة القائمة من النظام'
    ],
    notes: [
        'الصفحة للعرض فقط: لا إضافة ولا تعديل ولا حذف لطرق الدفع من هنا.',
        'زرّا الطباعة والتنزيل معروضان لكنهما لا ينفّذان أي إجراء حاليًا.'
    ],
    related: [
        { path: '/Financial', label: 'طلبات السحب والقيود اليدوية' },
        { path: '/FinancialRecharge', label: 'شحن المحافظ' },
        { path: '/VoucherCardsDistributors', label: 'الموزعون' }
    ]
},

'/PricingChangeLog': {
    tagline: 'من غيّر أي سعر ومتى وما القيمة السابقة',
    what: 'سجل تدقيق لكل تعديل يجري على إعدادات التسعير. عند كل عملية حفظ يُكتب سطر مستقل لكل حقل تغيّرت قيمته، يحمل اسم المسؤول والفئة واسم الحقل والقيمة قبل التعديل وبعده.',
    shows: [
        'جدول: تاريخ التعديل ووقته واسم المسؤول الذي نفّذه',
        'فئة الرحلة التي جرى تعديل تسعيرها',
        'اسم الحقل المعدَّل والقيمة السابقة والقيمة الجديدة جنبًا إلى جنب',
        'فلاتر: من تاريخ، وإلى تاريخ، ورقم المسؤول',
        'عدّاد إجمالي السجلات وترقيم للصفحات بعشرين سجلًا في الصفحة'
    ],
    actions: [
        'تحديد فترة زمنية بحقلي «من تاريخ» و«إلى تاريخ»',
        'إدخال رقم المسؤول لقصر السجل على تعديلاته وحده',
        '«تحديث» لإعادة قراءة السجل، و«العودة لإعدادات التسعير» للانتقال للوحة التسعير'
    ],
    notes: [
        'السجل للقراءة فقط ولا يمكن حذف أي قيد منه أو تعديله.',
        'عملية حفظ واحدة قد تُنتج عدة أسطر بعدد الحقول التي تغيّرت.'
    ],
    related: [
        { path: '/Pricing', label: 'التسعير الديناميكي' },
        { path: '/TripCategories', label: 'فئات الرحلات' }
    ]
},

'/VehicleTypes': {
    tagline: 'قاموس أنواع المركبات المعتمدة في المنظومة',
    what: 'صفحة مرجعية تضم قائمة أنواع المركبات التي يعتمد عليها النظام. كل سجل يحمل اسم النوع وهو حقل إلزامي، ووصفًا اختياريًا، وصورة اختيارية، إضافة إلى حالته وعدد المركبات المرتبطة به. تُدار القائمة بالكامل من هذه الصفحة إضافةً وتعديلًا وتفعيلًا وحذفًا.',
    shows: [
        'ثلاث بطاقات علوية: إجمالي الأنواع، وعدد النشط، وعدد غير النشط',
        'جدول بأعمدة: التسلسل، اسم النوع مع صورته، الوصف، عدد المركبات، الحالة، الإعدادات',
        'شارة ملوّنة بعدد المركبات المرتبطة بكل نوع',
        'حالة كل سجل ظاهرة كـ«نشط» أو «غير نشط»',
        'تلميح عند أيقونة الساعة يبيّن منفذ العملية وتاريخ الإدراج'
    ],
    actions: [
        'إضافة نوع مركبة جديدة: الاسم مطلوب، والوصف والصورة اختياريان',
        'تعديل السجل من قائمة النقاط الثلاث لتغيير الاسم أو الوصف أو الصورة',
        'تفعيل السجل أو إيقافه حسب حالته الحالية بعد رسالة تأكيد',
        'حذف السجل نهائيًا بعد رسالة تأكيد',
        'فلترة فورية بالكتابة في حقل «فلترة البيانات» مع تنقل بين الصفحات وزر تحديث'
    ],
    notes: [
        'الصفحة متاحة لمدير النظام فقط',
        'زرّا الطباعة والتنزيل ظاهران في الشريط لكنهما غير مفعّلين'
    ],
    related: [
        { path: '/Vehicles', label: 'المركبات' },
        { path: '/Settings', label: 'الإعدادات العامة' }
    ]
},

'/Cities': {
    tagline: 'قاموس المدن التي تغطيها الخدمة',
    what: 'القائمة المرجعية لمدن الخدمة، وهي المستوى الأول في التقسيم الجغرافي الذي تتفرّع منه البلديات ثم المناطق. كل سجل يحمل اسم المدينة وهو حقل إلزامي، ووصفًا اختياريًا، وصورة اختيارية، ويعرض عدد البلديات التابعة له.',
    shows: [
        'ثلاث بطاقات علوية: إجمالي المدن، وعدد النشط، وعدد غير النشط',
        'جدول بأعمدة: التسلسل، اسم المدينة مع صورتها، الوصف، عدد البلديات، الحالة، الإعدادات',
        'شارة بعدد البلديات المرتبطة بكل مدينة',
        'حالة كل مدينة ظاهرة كـ«نشط» أو «غير نشط»',
        'تلميح عند أيقونة الساعة يبيّن منفذ العملية وتاريخ الإدراج'
    ],
    actions: [
        'إضافة مدينة جديدة: الاسم مطلوب، والوصف والصورة اختياريان',
        'تعديل بيانات المدينة من قائمة النقاط الثلاث',
        'تفعيل المدينة أو إيقافها بعد رسالة تأكيد',
        'حذف المدينة نهائيًا بعد رسالة تأكيد',
        'فلترة فورية بالاسم مع تنقل بين الصفحات وزر تحديث'
    ],
    notes: [
        'حذف مدينة أو إيقافها يؤثر على البلديات والمناطق المبنية عليها',
        'الصفحة متاحة لمدير النظام فقط'
    ],
    related: [
        { path: '/Municipalities', label: 'البلديات' },
        { path: '/Locations', label: 'المواقع' }
    ]
},

'/Municipalities': {
    tagline: 'بلديات كل مدينة والمستوى الأوسط في التقسيم الجغرافي',
    what: 'تعرض البلديات وتربط كل بلدية بمدينة واحدة. عند الإضافة يجب اختيار المدينة وكتابة اسم البلدية، أما الوصف والصورة فاختياريان. ويظهر لكل بلدية عدد المناطق التابعة لها.',
    shows: [
        'ثلاث بطاقات علوية: إجمالي البلديات، وعدد النشط، وعدد غير النشط',
        'جدول بأعمدة: التسلسل، اسم البلدية مع صورتها ووصفها، المدينة، عدد المناطق، الحالة',
        'قائمة اختيار للتصفية حسب المدينة تبدأ بخيار «كل المدن»',
        'حالة كل بلدية ظاهرة كـ«نشط» أو «غير نشط»',
        'تلميح عند أيقونة الساعة يبيّن تاريخ الإدراج'
    ],
    actions: [
        'إضافة بلدية جديدة بعد اختيار المدينة وكتابة الاسم',
        'تعديل البلدية أو نقلها إلى مدينة أخرى من نافذة التعديل',
        'تفعيل البلدية أو إيقافها بعد رسالة تأكيد',
        'حذف البلدية نهائيًا بعد رسالة تأكيد',
        'التصفية بالاسم أو بالمدينة مع تنقل بين الصفحات وزر تحديث'
    ],
    notes: [
        'كل بلدية تابعة لمدينة واحدة، فيجب إنشاء المدينة أولًا',
        'المناطق مبنية على البلديات، فحذف بلدية يؤثر على مناطقها'
    ],
    related: [
        { path: '/Cities', label: 'المدن' },
        { path: '/Locations', label: 'المواقع' }
    ]
},

'/Locations': {
    tagline: 'مناطق ومواقع كل بلدية وأدق مستوى جغرافي',
    what: 'أدق مستوى في التقسيم الجغرافي، إذ ترتبط كل منطقة ببلدية واحدة، والبلدية بدورها بمدينة. عند الإضافة يجب اختيار البلدية وكتابة اسم المنطقة، والوصف والصورة اختياريان.',
    shows: [
        'ثلاث بطاقات علوية: إجمالي المناطق، وعدد النشط، وعدد غير النشط',
        'جدول بأعمدة: التسلسل، اسم المنطقة مع صورتها ووصفها، البلدة، المدينة، الحالة',
        'قائمة اختيار للتصفية حسب البلدية تبدأ بخيار «كل البلديات»',
        'حالة كل منطقة ظاهرة كـ«نشط» أو «غير نشط»',
        'تلميح عند أيقونة الساعة يبيّن تاريخ الإدراج'
    ],
    actions: [
        'إضافة منطقة جديدة بعد اختيار البلدية وكتابة الاسم',
        'تعديل المنطقة أو نقلها إلى بلدية أخرى من نافذة التعديل',
        'تفعيل المنطقة أو إيقافها بعد رسالة تأكيد',
        'حذف المنطقة نهائيًا بعد رسالة تأكيد',
        'التصفية بالاسم أو بالبلدية مع تنقل بين الصفحات وزر تحديث'
    ],
    notes: [
        'اسم المدينة يظهر تلقائيًا اعتمادًا على البلدية المختارة',
        'الصفحة متاحة لمدير النظام فقط'
    ],
    related: [
        { path: '/Municipalities', label: 'البلديات' },
        { path: '/Cities', label: 'المدن' }
    ]
},

'/Faq': {
    tagline: 'طلبات وأسئلة المستخدمين الواردة إلى الإدارة',
    what: 'سجل الطلبات والأسئلة التي يرسلها المستخدمون، ويتابع منه المسؤول ما هو جديد وما تمت معالجته. لا يمكن إنشاء سجل من هنا لأن المحتوى يأتي من المستخدمين أنفسهم، ويقتصر دور المسؤول على إنهاء معالجة الطلب أو حذفه.',
    shows: [
        'أربع بطاقات: عدد الطلبات، والطلبات الجديدة، وما تمت الموافقة عليه، والمرفوض',
        'جدول بأعمدة: التسلسل، حالة الطلب، مقدم الطلب، الرسالة، تاريخ الطلب، منفذ العملية',
        'بيانات مقدم الطلب تشمل بريده الإلكتروني ورقم هاتفه',
        'شارة الحالة: «طلب جديد» أو «تمت الموافقة» أو «تم رفض الطلب»',
        'قائمة لتحديد عدد الصفوف المعروضة من عشرة إلى ألف'
    ],
    actions: [
        'إنهاء معالجة الطلب بالضغط على علامة الصح، ويظهر للطلبات الجديدة فقط',
        'حذف الطلب نهائيًا بعد رسالة تأكيد',
        'البحث في الطلبات وتغيير عدد الصفوف والتنقل بين الصفحات'
    ],
    notes: [
        'الصفحة عرض ومعالجة فقط ولا تحتوي على نموذج إضافة',
        'الصفحة متاحة لمدير النظام فقط'
    ],
    related: [
        { path: '/Reports', label: 'سجل البلاغات والشكاوى' },
        { path: '/Settings', label: 'الإعدادات العامة' }
    ]
},

'/SmsSimulator': {
    tagline: 'سجل الرسائل النصية التي كان النظام سيرسلها للاختبار',
    what: 'شاشة اختبار تعرض على شكل خط زمني نصوص الرسائل التي يولّدها النظام مثل رموز التحقق ورسائل الترحيب ورسائل مراحل الرحلة ورسائل الطوارئ. تفيد فريق الاختبار في قراءة رمز التحقق أو كلمة المرور المؤقتة دون الحاجة إلى هاتف حقيقي.',
    shows: [
        'شارة تبيّن وضع الرسائل الحالي: محاكاة أو إرسال حقيقي أو معطّل',
        'خط زمني لكل رسالة يعرض نوعها وقناتها ورقم المستقبل ووقت الإرسال',
        'نص الرسالة كاملًا كما سيصل إلى المستخدم',
        'رمز التحقق وكلمة المرور المؤقتة في شارتين منفصلتين عند وجودهما',
        'مؤشرات إضافية لمصدر الرسالة داخل النظام ورقم تتبعها',
        'تنبيه واضح عندما تكون الخدمة غير متاحة على الخادم'
    ],
    actions: [
        'نسخ رمز التحقق أو كلمة المرور المؤقتة بنقرة واحدة',
        'التصفية برقم الهاتف كاملًا أو جزئيًا',
        'التصفية برقم الرحلة أو برقم الطالب الراكب',
        'تحديد تاريخ البداية وعدد الصفوف المعروضة',
        'الضغط على «تحديث» لجلب أحدث الرسائل'
    ],
    notes: [
        'القائمة تُحدَّث تلقائيًا كل خمس ثوانٍ',
        'الأوقات معروضة بتوقيت طرابلس',
        'الصفحة متاحة لمدير النظام فقط لأنها تكشف رموز كل المستخدمين'
    ],
    related: [
        { path: '/Settings', label: 'الإعدادات العامة' },
        { path: '/Users', label: 'المستخدمون والصلاحيات' }
    ]
},

'/Settings': {
    tagline: 'مركز يجمع روابط صفحات إعدادات النظام',
    what: 'صفحة تجميعية لا تحتوي على حقول إعداد بذاتها، بل تعرض بطاقات تنقلك مباشرة إلى الصفحة المختصة بكل إعداد. الغرض منها اختصار الوصول إلى إعدادات التسعير والعمولات والقواميس دون البحث في القوائم الجانبية.',
    shows: [
        'بطاقة التسعير الديناميكي وبطاقة قواعد العمولة',
        'بطاقة أنواع الرحلات والمركبات وبطاقة أنواع الاشتراكات',
        'بطاقة طرق الدفع وبطاقة سياسة الإلغاء',
        'بطاقة المدن والبلديات والمواقع لضبط النطاق الجغرافي',
        'بطاقة الأسئلة الشائعة وبطاقة محاكي الرسائل'
    ],
    actions: [
        'الضغط على أي بطاقة للانتقال إلى صفحة الإعداد المقابلة',
        'استخدام مفتاح الإدخال للانتقال عند التنقل بلوحة المفاتيح'
    ],
    notes: [
        'لا يتم حفظ أي بيانات من هذه الصفحة، فهي مجرد نقطة انطلاق'
    ],
    related: [
        { path: '/Pricing', label: 'التسعير الديناميكي' },
        { path: '/Cities', label: 'المدن' },
        { path: '/VehicleTypes', label: 'أنواع المركبات' }
    ]
},

'/Users': {
    tagline: 'إدارة حسابات لوحة التحكم وصلاحياتها',
    what: 'الصفحة الرئيسية لإدارة حسابات النظام. منها تُنشأ الحسابات وتُعدَّل بياناتها وتُهيَّأ كلمات المرور وتُعلَّق أو تُفعَّل أو تُحذف. كما يمكن فتح ملف أي مستخدم لعرض بياناته وأرصدته وسجل عملياته.',
    shows: [
        'أربع بطاقات: إجمالي الحسابات وعددها حسب النوع مدير ومستخدم وموزع',
        'جدول بأعمدة: التسلسل، اسم المستخدم مع صورته وهاتفه، البريد الإلكتروني، نوع المستخدم، الحالة',
        'حالة الحساب: مفعّل أو موقوف أو معلّق',
        'تلميح يبيّن منفذ العملية وتاريخ الإنشاء وتاريخ آخر دخول',
        'ملف المستخدم عند فتحه يعرض رصيد المحفظة وعدد العمليات وعدد الإشعارات',
        'تبويب سجل العمليات داخل الملف بأعمدة: العملية والقسم والوصف والتاريخ'
    ],
    actions: [
        'إضافة مستخدم جديد بالاسم ورقم الهاتف والبريد ونوع الحساب وصورة اختيارية',
        'تعديل بيانات الحساب من قائمة النقاط الثلاث',
        'تهيئة كلمة المرور للحساب بعد رسالة تأكيد',
        'تعليق الحساب مع إلزامية كتابة سبب التعليق، أو إلغاء التعليق',
        'تفعيل الحساب أو إيقافه حسب حالته الحالية',
        'حذف الحساب نهائيًا بعد رسالة تأكيد',
        'البحث وتغيير عدد الصفوف والتنقل بين الصفحات'
    ],
    notes: [
        'خيارا «عرض البيانات المالية» و«تسليم قيمة مالية» ظاهران في القائمة دون وظيفة',
        'الصفحة متاحة لمدير النظام فقط'
    ],
    related: [
        { path: '/UsersSuspend', label: 'الحسابات الموقوفة' },
        { path: '/UsersProfile', label: 'ملفي الشخصي' },
        { path: '/UsersNotifications', label: 'صندوق الإشعارات' }
    ]
},

'/UsersSuspend': {
    tagline: 'الحسابات المعلَّقة مع أسباب التعليق ورفعه',
    what: 'قائمة الحسابات المعلَّقة حاليًا مع سبب التعليق المسجَّل لكل حساب. الإجراء الوحيد المتاح فيها هو رفع التعليق، أما التعليق نفسه فيُنفَّذ من صفحة المستخدمين.',
    shows: [
        'ثلاث بطاقات: عدد النشط وعدد الملغي وعدد الموقوف',
        'جدول بأعمدة: التسلسل، المستخدم، البريد الإلكتروني، نوع المستخدم، الحالة، سبب التعليق',
        'اسم المستخدم ورقم هاتفه في خانة واحدة',
        'سبب التعليق مختصرًا مع تلميح يعرض النص كاملًا',
        'نوع المستخدم مبيَّنًا بشارة ملوّنة'
    ],
    actions: [
        'الضغط على «إلغاء التعليق» لإعادة تفعيل الحساب بعد رسالة تأكيد',
        'البحث بالاسم أو البريد الإلكتروني',
        'تحديث القائمة وتغيير عدد الصفوف والتنقل بين الصفحات'
    ],
    notes: [
        'الصفحة تعرض التعليقات السارية فقط ولا تعرض السجل التاريخي',
        'الصفحة متاحة لمدير النظام فقط'
    ],
    related: [
        { path: '/Users', label: 'المستخدمون والصلاحيات' }
    ]
},

'/UsersProfile': {
    tagline: 'تعديل بياناتك الشخصية وصورتك وكلمة مرورك',
    what: 'الصفحة الخاصة بحسابك أنت وليس بحسابات الآخرين. تنقسم إلى تبويبين: تبويب البيانات الشخصية لتحديث الاسم والهاتف والبريد والصورة، وتبويب تغيير كلمة المرور.',
    shows: [
        'ترويسة تعرض صورتك واسمك واسم الدخول',
        'رقم هاتفك وبريدك الإلكتروني كما هما مسجّلان حاليًا',
        'شارة تبيّن دورك في المنظومة',
        'نموذج البيانات الشخصية بحقول الاسم والهاتف والبريد والصورة',
        'صندوق تنبيه يوضّح متطلبات كلمة المرور الجديدة'
    ],
    actions: [
        'الضغط على «حفظ التغييرات» لتحديث الاسم ورقم الهاتف والبريد الإلكتروني',
        'اختيار صورة شخصية بصيغة صورة معتمدة لرفعها',
        'تغيير كلمة المرور بإدخال الحالية والجديدة وتأكيدها ثم «تهيئة كلمة المرور»'
    ],
    notes: [
        'الصورة تُرفع فور اختيارها دون الحاجة إلى الضغط على زر الحفظ',
        'كلمة المرور الجديدة لا تقل عن ثماني خانات وتضم حروفًا كبيرة وصغيرة وأرقامًا',
        'لا يُقبل أن تكون كلمة المرور الجديدة مطابقة للحالية'
    ],
    related: [
        { path: '/Users', label: 'المستخدمون والصلاحيات' },
        { path: '/UsersNotifications', label: 'صندوق الإشعارات' }
    ]
},

'/UsersNotifications': {
    tagline: 'صندوق الإشعارات الواردة إلى حسابك',
    what: 'يعرض الإشعارات الواردة إلى حسابك مع حالة قراءة كل إشعار. فتح أي إشعار يُظهر تفاصيله كاملة ويعلّمه كمقروء تلقائيًا. كما تتيح الصفحة نافذة لإنشاء إشعار وإرساله.',
    shows: [
        'أربع بطاقات: الإجمالي والنشط وغير النشط والمحذوف',
        'جدول بأعمدة: التسلسل، الاسم مع الصورة والهاتف، نوع الإشعار، العنوان، حالة القراءة',
        'شارة القراءة: «تم القراءة» أو «لم يتم القراءة»',
        'تلميح يبيّن منفذ العملية وتاريخ الإرسال وتاريخ القراءة',
        'نافذة التفاصيل تعرض النوع والعنوان والمحتوى وحالة القراءة والتواريخ'
    ],
    actions: [
        'فتح تفاصيل الإشعار، ويُعلَّم تلقائيًا كمقروء إن لم يكن كذلك',
        'إنشاء إشعار جديد بتحديد نوعه وكتابة عنوانه ومحتواه',
        'البحث في الإشعارات وتغيير عدد الصفوف والتنقل بين الصفحات'
    ],
    notes: [
        'بعض خيارات الاستهداف في نافذة الإرسال موروثة ولا تناسب طبيعة رفيق',
        'للبث الجماعي استخدم صفحة إرسال إشعار عام بدل هذه النافذة'
    ],
    related: [
        { path: '/AdminNotifications', label: 'إرسال إشعار عام' },
        { path: '/SendNotificationByTarget', label: 'إشعار موجّه' }
    ]
},

'/AdminNotifications': {
    tagline: 'بثّ إشعار جماعي لكل المستخدمين أو لدور محدد',
    what: 'صفحة تحرير وإرسال إشعار جماعي. يُكتب الإشعار في صندوق إشعارات المستخدمين ويُدفع في الوقت نفسه إلى الأجهزة المسجَّلة. يمكن توجيهه لكل المستخدمين أو لدور بعينه، مع إمكانية قصره على مدرسة واحدة.',
    shows: [
        'حقل عنوان الإشعار بحد أقصى ثمانين حرفًا',
        'تبويبات نوع المستلم: الكل، الركاب، السائقون، الأهالي، مسؤولو المدارس',
        'حقل نص الإشعار بحد أقصى خمسمئة حرف',
        'حقل اختياري لرقم المدرسة لقصر الإشعار على طلابها وأهاليهم',
        'بطاقة معاينة حيّة تُظهر شكل الإشعار والفئة المستهدفة',
        'بطاقة نتيجة تبيّن عدد الرسائل المكتوبة وعدد الإشعارات المدفوعة'
    ],
    actions: [
        'الضغط على «إرسال الإشعار» بعد تعبئة العنوان والنص',
        'الضغط على «مسح» لتفريغ الحقول وإعادة البدء',
        'اختيار الفئة المستهدفة أو تركها على «الكل»'
    ],
    notes: [
        'زر الإرسال يبقى معطّلًا حتى تُكتب حقول العنوان والنص معًا',
        'الصفحة متاحة لمدير النظام ولمسؤولي المدارس ضمن نطاق مدرستهم'
    ],
    related: [
        { path: '/SendNotificationByTarget', label: 'إشعار موجّه' },
        { path: '/UsersNotifications', label: 'صندوق الإشعارات' }
    ]
},

'/SendNotificationByTarget': {
    tagline: 'إنشاء إشعار داخل التطبيق لفئة أو لمستخدم محدد',
    what: 'نموذج لإرسال إشعار داخل التطبيق إلى فئة مختارة من المستخدمين أو إلى مستخدم واحد بعينه. يختلف عن البثّ الجماعي في أنه يُنشئ الإشعار داخل التطبيق فقط دون دفعه إلى الأجهزة، ويوفّر خيارات استهداف أدق تشمل المدارس.',
    shows: [
        'حقل عنوان الإشعار بحد أقصى مئتي حرف مع عدّاد للأحرف',
        'حقل محتوى الإشعار بحد أقصى ألف حرف مع عدّاد للأحرف',
        'قائمة الفئة المستهدفة: كل المستخدمين أو السائقين أو الركاب أو المدارس أو أولياء الأمور',
        'خيارات إضافية لطلاب مدرسة محددة أو أولياء أمور مدرسة محددة أو مستخدم واحد',
        'قائمة اختيار المدرسة تظهر فقط عند الخيارين المدرسيين',
        'حقل رقم المستخدم يظهر فقط عند اختيار مستخدم واحد',
        'شريط نتيجة يعرض رسالة النجاح مع عدد المستفيدين أو سبب الفشل'
    ],
    actions: [
        'اختيار الفئة المستهدفة وتعبئة العنوان والمحتوى ثم إرسال الإشعار',
        'تحديد المدرسة أو رقم المستخدم عندما يتطلب الاستهداف ذلك',
        'إعادة تعيين النموذج لتفريغ كل الحقول'
    ],
    notes: [
        'الإشعار يُنشأ داخل التطبيق ولا يُدفع إشعارًا فوريًا إلى الأجهزة',
        'واجهة هذه الصفحة معروضة باللغة الإنجليزية',
        'العنوان والمحتوى والفئة حقول إلزامية قبل الإرسال'
    ],
    related: [
        { path: '/AdminNotifications', label: 'إرسال إشعار عام' },
        { path: '/UsersNotifications', label: 'صندوق الإشعارات' }
    ]
},

'/Reports': {
    tagline: 'متابعة بلاغات المستخدمين والرد عليها وإغلاقها',
    what: 'طابور البلاغات المقدَّمة من المستخدمين من شكاوى وملاحظات وحوادث. يمكن للمسؤول قراءة البلاغ مع كامل سلسلة الرسائل، والرد على المُبلِّغ، أو تسجيل ملاحظة داخلية لا تصل إليه، أو إغلاق البلاغ باعتباره محلولًا.',
    shows: [
        'أربع بطاقات: بلاغات جديدة، وقيد المعالجة، وتم الرد عليها، ومحلولة',
        'قائمة تصفية بالحالات: جديدة، مُسندة، تم الرد، محلولة، مرفوضة، مُصعَّدة',
        'جدول بأعمدة: التسلسل، المُبلِّغ، الموضوع، الحالة، تاريخ التقديم، إجراءات',
        'اسم المُبلِّغ ورقم هاتفه في خانة واحدة',
        'نافذة البلاغ تعرض نصه الأصلي وسلسلة الرسائل مرتبة زمنيًا',
        'تمييز واضح بين رسائل الإدارة ورسائل المُبلِّغ والملاحظات الداخلية'
    ],
    actions: [
        'الضغط على «عرض» لفتح البلاغ وقراءة سلسلة الرسائل',
        'الضغط على «حل» لإغلاق البلاغ مباشرة من الجدول',
        'كتابة رد على المُبلِّغ ثم الضغط على «إرسال»',
        'تحديد خيار «ملاحظة داخلية» ليُسجَّل الرد دون أن يصل المُبلِّغ',
        'تصفية الطابور بحالة معيّنة وتحديث القائمة والتنقل بين الصفحات'
    ],
    notes: [
        'زر «حل» لا يظهر للبلاغات المحلولة أو المرفوضة',
        'صندوق الرد يختفي بعد إغلاق البلاغ أو رفضه',
        'الصفحة متاحة لمدير النظام فقط'
    ],
    related: [
        { path: '/Sos', label: 'بلاغات الطوارئ' },
        { path: '/Users', label: 'المستخدمون والصلاحيات' }
    ]
},

'/TrustedContacts': {
    tagline: 'عرض جهات الطوارئ المسجَّلة لمستخدم معيّن',
    what: 'شاشة استعلام تعرض جهات الاتصال الموثوقة الخاصة بمستخدم واحد، وهي الجهات التي تُبلَّغ برسالة نصية عند تفعيل المستخدم زر الطوارئ. الصفحة للعرض فقط، والإضافة والتعديل يتمان من تطبيق المستخدم نفسه.',
    shows: [
        'حقل لإدخال رقم المستخدم المطلوب استعراض جهاته',
        'حقل بحث داخل النتائج بالاسم أو رقم الهاتف أو صلة القرابة',
        'جدول بأعمدة: التسلسل، اسم جهة الاتصال، رقم الهاتف، صلة القرابة',
        'عمود يبيّن هل تُرسَل رسالة إلى الجهة عند الطوارئ بنعم أو لا',
        'عمود حالة الجهة: مفعّل أو معطّل، مع تاريخ الإنشاء',
        'تنبيه واضح عندما تكون الخدمة غير متاحة في لوحة الإدارة'
    ],
    actions: [
        'إدخال رقم المستخدم ثم الضغط على «تحديث» لجلب جهاته الموثوقة',
        'تصفية النتائج المعروضة عبر حقل البحث'
    ],
    notes: [
        'لا تُعرض أي بيانات قبل إدخال رقم مستخدم محدد',
        'الصفحة عرض فقط ولا تسمح بالإضافة أو التعديل أو الحذف',
        'الصفحة متاحة لمدير النظام فقط'
    ],
    related: [
        { path: '/Sos', label: 'بلاغات الطوارئ' },
        { path: '/Users', label: 'المستخدمون والصلاحيات' }
    ]
}
,

// ===================== الرحلات التعاقدية =====================
'/ContractRequests': {
    tagline: 'مراجعة طلبات التعاقد وإصدار عروض الأسعار واعتمادها',
    what: 'هذه الصفحة هي بداية دورة «الرحلة المخصصة»: العميل — فرداً كان أو شركة — يرسل طلب تعاقد يحدد فيه المسار والجدول والمدة وعدد الركاب، وأنت تراجعه وتصدر له عرض سعر بمدة صلاحية. توجد لأن العقد التزام طويل الأجل لا يمكن تسعيره لحظياً كرحلة عادية. النظام يحسب سعراً مقترحاً من المسافة وأيام التشغيل ونوع الخدمة وعدد الركاب ونقاط التوقف والذروة ومرونة العودة وحصرية السائق، لكن السعر لا يُرسل للعميل قبل اعتمادك له.',
    shows: [
        'قائمة الطلبات مع فلاتر: الحالة (مسودة، قيد المراجعة، يحتاج معلومات، صدر عرض، انتظار الدفع، مقبول، مرفوض، منتهي الصلاحية)',
        'نوع العميل: فرد أو شركة، مع اسمه ومسار الرحلة وعدد الركاب والمدة المطلوبة',
        'شاشة التفاصيل: نقاط المسار على الخريطة، المسافة اليومية المحسوبة، وكل نسخ عروض الأسعار السابقة',
        'تفكيك السعر المقترح عاملاً بعامل: السعر الأساسي، معامل نوع الخدمة، الركاب، التوقفات، الذروة، مرونة العودة، الحصرية'
    ],
    actions: [
        'اضغط «بدء المراجعة» لنقل الطلب من «تم الإرسال» إلى «قيد المراجعة»',
        'اضغط «طلب معلومات» لإعادته للعميل مع ملاحظة توضح الناقص',
        'راجع السعر المقترح، وعدّله إن لزم — القيمة تُحفظ بدينار صحيح لأن المحفظة لا تقبل الكسور',
        'اضغط «اعتماد وإرسال» لإرسال العرض للعميل؛ لا يمكن الإرسال قبل الاعتماد',
        'إصدار عرض جديد ينشئ نسخة أحدث ويجعل السابقة «مُستبدَلة» — لا تُعدَّل نسخة أرسلت للعميل'
    ],
    notes: [
        'العرض ينتهي تلقائياً بعد تاريخ صلاحيته، ولا يمكن للعميل قبوله بعدها',
        'قبول العميل للعرض هو ما ينشئ العقد ويخصم أول دورة من محفظته'
    ],
    related: [
        { path: '/Contracts', label: 'العقود' },
        { path: '/ContractCompanies', label: 'الشركات والموظفون' }
    ]
},

'/Contracts': {
    tagline: 'إدارة العقود النشطة: المجموعات والجدول والركاب وإسناد السائقين',
    what: 'العقد هو الغلاف التجاري، أما وحدة التشغيل الحقيقية فهي «مجموعة النقل»: كل مجموعة لها جدولها الأسبوعي وركابها وسائقها ومركبتها. عقد شركة بأربعين موظفاً يُقسَّم إلى عدة مجموعات تماماً كما تُقسَّم مدرسة كبيرة إلى عدة مسارات. عقد الفرد له مجموعة واحدة تُنشأ تلقائياً.',
    shows: [
        'قائمة العقود بحالاتها: لم يبدأ، نشط، موقوف مؤقتاً، فترة سماح، متأخر في الدفع، يحتاج تعديل، انتظار التجديد، منتهٍ',
        'تفاصيل العقد بتبويبات: المجموعات، الجدول الأسبوعي، الركاب، الإسناد، التعديلات، الفواتير',
        'سياسات العقد الإلزامية: مدة الانتظار المجاني، رسوم الانتظار، سياسة العطل والإلغاء والاسترداد',
        'سجل الإسناد كاملاً: من كان السائق المسؤول في كل فترة، بما فيها البدائل المؤقتة'
    ],
    actions: [
        'حدّد السياسات ثم اضغط «تفعيل» — لا يمكن تفعيل عقد قبل استيفاء كل السياسات ونجاح الدفع أو اعتماد الائتمان',
        'أنشئ مجموعات وأضف لكل مجموعة مواعيدها وركابها',
        'أسند سائقاً ومركبة: النظام يفحص تعارض الجدول مع العقود والمسارات المدرسية معاً، وسعة المركبة، وصلاحية وثائقها حتى نهاية الإسناد',
        'استخدم «بحث السائقين المتاحين» لعرض المرشحين مع سبب استبعاد كل مرشح غير صالح',
        'عيّن بديلاً مؤقتاً لفترة محددة — لا يحتاج موافقة جديدة من العميل في الطوارئ',
        'أوقف العقد مؤقتاً أو أنهه أو جدّده'
    ],
    notes: [
        'حذف راكب أو تعديل موعد لا يمحو الماضي: النظام يغلق الصف القديم بتاريخ ويضيف صفاً جديداً، فتبقى الرحلات المنفذة قابلة للتفسير',
        'تطبيق تعديل يُعيد توليد الأيام المستقبلية فقط ولا يمس رحلة نُفذت أو فاتورة أُغلقت',
        'السائق لا يستطيع إلغاء العقد؛ يمكنه فقط الإبلاغ عن تعذر رحلة أو فترة'
    ],
    related: [
        { path: '/ContractRequests', label: 'الطلبات وعروض الأسعار' },
        { path: '/ContractPlanning', label: 'خطة 30 يومًا' },
        { path: '/ContractInvoices', label: 'فواتير العقود' }
    ]
},

'/ContractCompanies': {
    tagline: 'بيانات الشركات وموظفيها وحدودها الائتمانية',
    what: 'الشركات عملاء العقود المؤسسية. في الإصدار الحالي لا توجد بوابة دخول للشركات، لذلك يدير موظفو رفيق بيانات الشركة وموظفيها نيابة عنها من هنا. قائمة الموظفين تعيش أطول من أي عقد، فتجديد العقد لا يعني إعادة إدخال نقاط ركوب الجميع من جديد.',
    shows: [
        'قائمة الشركات: الاسم، مسؤول التواصل، مسؤول الفوترة، الحد الائتماني، فترة السماح',
        'ملخص الانكشاف الائتماني: مجموع الفواتير غير المدفوعة والمتأخرة مقابل الحد الائتماني',
        'قائمة موظفي الشركة: الاسم، الهاتف، نقطة الركوب والنزول الافتراضية، الوردية'
    ],
    actions: [
        'أضف شركة وحدد حدها الائتماني وفترة سماحها (3–7 أيام حسب العقد)',
        'أضف الموظفين يدوياً أو استورد ملف Excel',
        'اربط الموظف بحساب راكب إن كان يستخدم التطبيق — عندها يستطيع تتبع رحلته'
    ],
    notes: [
        'أغلب الموظفين ليسوا مستخدمي تطبيق، ويظهرون في الرحلة كركاب ضيوف',
        'تجاوز الحد الائتماني يمنع تفعيل عقود جديدة للشركة حتى تسدد'
    ],
    related: [
        { path: '/Contracts', label: 'العقود' },
        { path: '/ContractInvoices', label: 'فواتير العقود' }
    ]
},

'/ContractPlanning': {
    tagline: 'الرحلات المخططة للثلاثين يوماً القادمة والأيام التي بلا سائق',
    what: 'النظام يولّد خطة متحركة لثلاثين يوماً من جدول كل مجموعة، ثم يحوّل خطة الغد إلى رحلات حقيقية قبل يوم واحد فقط. هذه الصفحة تعرض الخطة قبل أن تصبح رحلات، فتستطيع اكتشاف يوم بلا سائق أو تعارض قبل أن يفاجئك صباحاً.',
    shows: [
        'الأيام المخططة لكل مجموعة مع الوقت والاتجاه (ذهاب أو عودة) والسائق المحلول',
        'الأيام غير المُسندة — تحتاج تعيين سائق قبل موعدها',
        'الأيام الملغاة أو المتخطاة بسبب عطلة أو إيقاف، مع سبب كل حالة'
    ],
    actions: [
        'عيّن سائقاً لمجموعة لسد الأيام غير المُسندة',
        'أضف عطلة أو إيقافاً فيُلغى ما خُطط داخل المدة تلقائياً',
        'عدّل وقت يوم واحد دون تغيير الجدول الأسبوعي'
    ],
    notes: [
        'الخطة صفوف خفيفة وليست رحلات: تعديلها لا يُلغي رحلات حقيقية ولا يؤثر على سجل التزام السائق',
        'الرحلة الحقيقية تُنشأ قبل يوم من موعدها، ومنها فقط يبدأ التتبع والدردشة'
    ],
    related: [
        { path: '/Contracts', label: 'العقود' },
        { path: '/ContractTripsToday', label: 'متابعة اليوم' }
    ]
},

'/ContractTripsToday': {
    tagline: 'متابعة رحلات العقود اليوم والتصعيدات وحالة الوظائف الخلفية',
    what: 'لوحة التشغيل اليومية للرحلات التعاقدية: ما الذي يجري الآن، وما الذي يحتاج تدخلاً. تعرض أيضاً آخر تشغيل ناجح لوظائف التوليد والفوترة، لأن وظيفة توقفت بصمت تعني غياب رحلات الغد دون أي إشعار آخر.',
    shows: [
        'رحلات اليوم مجمّعة بحالتها: مجدولة، في انتظار التعيين، السائق في الطريق، وصل، في الانتظار، جارية، مكتملة',
        'التصعيدات: رحلة بلا سائق، سائق أبلغ بتعذر التنفيذ، عطل مركبة، حالة طارئة',
        'حالة كل راكب: صعد، ألغى مسبقاً، لم يحضر، في إجازة',
        'شريط حالة الوظائف: آخر تشغيل ناجح لتوليد الخطة والتجسيد والفوترة'
    ],
    actions: [
        'أعد إسناد رحلة يوم واحد لسائق آخر',
        'ضع علامة على الرحلة: عطل، طارئة، استبدال',
        'ألغِ يوماً واحداً دون المساس بالعقد',
        'أضف رحلة إضافية مرتبطة بالعقد (دوام إضافي في عطلة)'
    ],
    notes: [
        'تحذير «لا يوجد تشغيل ناجح خلال 26 ساعة» يعني توقف التوليد — راجع مفاتيح التشغيل في إعدادات الخادم',
        'عدم حضور الراكب لا يُعتمد إلا بعد وصول مثبت وانقضاء مهلة الانتظار ومحاولة اتصال مسجلة'
    ],
    related: [
        { path: '/ContractPlanning', label: 'خطة 30 يومًا' },
        { path: '/Trips', label: 'كل الرحلات' }
    ]
},

'/ContractInvoices': {
    tagline: 'فواتير العقود والمتأخرات وفترات السماح والتسويات',
    what: 'كل عقد يُفوتَر شهرياً. الأفراد يُخصم منهم مقدماً من المحفظة فتُنشأ فاتورتهم مدفوعة، والشركات تُفوتَر آجلاً بحد ائتماني وفترة سماح. عند تجاوز فترة السماح تصبح الفاتورة متأخرة ويُوقَف العقد تلقائياً حتى السداد.',
    shows: [
        'الفواتير بحالاتها: غير مدفوعة، مدفوعة، متأخرة، ملغاة',
        'الفترة (شهر/سنة)، قيمة العقد، التسويات، تاريخ الاستحقاق ونهاية فترة السماح',
        'قناة الدفع: محفظة، ائتمان شركة، أو تحصيل يدوي',
        'لوحة المتأخرات: العقود الموقوفة بسبب عدم الدفع'
    ],
    actions: [
        'اضغط «تعليم كمدفوعة» عند استلام حوالة — إن كانت آخر فاتورة متأخرة يُرفع الإيقاف ويعود العقد نشطاً تلقائياً',
        'أضف تسوية بقيمة موجبة أو سالبة مع ملاحظة (فرق سعر إضافة موظف، أو رصيد إنهاء مبكر)',
        'ألغِ فاتورة صدرت بالخطأ'
    ],
    notes: [
        'فاتورة صدرت متأخرة عن موعدها بسبب توقف النظام تحصل على فترة سماح جديدة من تاريخ إصدارها — التأخير ليس خطأ العميل',
        'أسعار العقود بدينار صحيح لأن المحفظة لا تخزّن كسوراً'
    ],
    related: [
        { path: '/Contracts', label: 'العقود' },
        { path: '/ContractCompanies', label: 'الشركات والموظفون' },
        { path: '/Financial', label: 'المالية' }
    ]
}
};
